import { useCallback, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Marker, Popup, Tooltip, Circle } from "react-leaflet";
import L from "leaflet";
import {
  Activity,
  Users,
  Target,
  Award,
  TrendingUp,
  MapPin,
  Bell,
  LogOut,
  Search,
  Filter,
  Layers,
  BarChart3,
  Map as MapIcon,
  Compass,
  Camera,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Settings,
  ChevronRight,
  ChevronDown,
  Radio,
  Building2,
  ShieldCheck,
  Download,
  X,
} from "lucide-react";
import { ModeToggle } from "../../components/ModeToggle";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
  Line,
  LineChart,
} from "recharts";

import { ROLE_LABELS, HOME_COORD, PROSPECT_STATUS } from "../../constants/config";
import { api } from "../../lib/api";
import { useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const STATUS_META = {
  valid: { label: "Valid", color: "#22C55E", bg: "rgba(34,197,94,0.15)" },
  expired: { label: "Perlu Revisit", color: "#E2231A", bg: "rgba(226,35,26,0.15)" },
  visiting: { label: "Kunjungan", color: "#22C55E", bg: "rgba(34,197,94,0.15)" },
  visited: { label: "Kunjungan Valid", color: "#22C55E", bg: "rgba(34,197,94,0.15)" },
  prospect: { label: "Prospek", color: "#2563EB", bg: "rgba(37,99,235,0.15)" },
  closing: { label: "Closing", color: "#D97706", bg: "rgba(217,119,6,0.15)" },
  out_of_area: { label: "Out of Coverage Area", color: "#991B1B", bg: "rgba(153,27,27,0.14)" },
  moving: { label: "Bergerak", color: "#3B82F6", bg: "rgba(59,130,246,0.15)" },
  idle: { label: "Idle", color: "#EAB308", bg: "rgba(234,179,8,0.15)" },
};

const PIE_COLORS = [
  "#E2231A",
  "#1A1A1A",
  "#F97316",
  "#3B82F6",
  "#22C55E",
  "#EAB308",
  "#8B5CF6",
  "#06B6D4",
];

const ACTIVITY_ICONS = {
  closing: { Icon: Award, color: "text-green-600", bg: "bg-green-50" },
  prospect: { Icon: Users, color: "text-blue-600", bg: "bg-blue-50" },
  evidence: { Icon: Camera, color: "text-honda-red", bg: "bg-honda-red-light" },
  status: { Icon: TrendingUp, color: "text-orange-600", bg: "bg-orange-50" },
  alert: { Icon: AlertTriangle, color: "text-yellow-700", bg: "bg-yellow-50" },
  followup: { Icon: Clock, color: "text-violet-600", bg: "bg-violet-50" },
};

const ADMIN_NAV = [
  { key: "dashboard", path: "/admin/dashboard", label: "Dashboard", icon: Activity, section: "Operasional" },
  { key: "live-map", path: "/admin/live-map", label: "Live Map", icon: MapIcon, section: "Operasional", badge: "live" },
  { key: "salesman", path: "/admin/salesman", label: "Salesman", icon: Users, section: "Operasional" },
  { key: "prospek", path: "/admin/prospek", label: "Prospek", icon: Target, section: "Operasional" },
  { key: "bukti-foto", path: "/admin/bukti-foto", label: "Bukti Foto", icon: Camera, section: "Operasional", badge: "HOT" },
  { key: "dealer", path: "/admin/dealer", label: "Dealer", icon: Building2, section: "Operasional" },
  { key: "users", path: "/admin/users", label: "Kelola User", icon: ShieldCheck, section: "Operasional" },
  { key: "analytics", path: "/admin/analytics", label: "Analytics", icon: BarChart3, section: "Intelligence" },
  { key: "coverage-map", path: "/admin/coverage-map", label: "Coverage Map", icon: Layers, section: "Intelligence" },
  { key: "audit-log", path: "/admin/audit-log", label: "Audit Log", icon: ShieldCheck, section: "Intelligence" },
  { key: "settings", path: "/admin/settings", label: "Settings", icon: Settings, section: "Intelligence" },
];

const MONTH_OPTIONS = [
  { value: "all", label: "Semua Bulan" },
  { value: "1", label: "Januari" },
  { value: "2", label: "Februari" },
  { value: "3", label: "Maret" },
  { value: "4", label: "April" },
  { value: "5", label: "Mei" },
  { value: "6", label: "Juni" },
  { value: "7", label: "Juli" },
  { value: "8", label: "Agustus" },
  { value: "9", label: "September" },
  { value: "10", label: "Oktober" },
  { value: "11", label: "November" },
  { value: "12", label: "Desember" },
];

const TARGET_YEAR_OPTIONS = Array.from({ length: 5 }, (_, index) => String(new Date().getFullYear() - 1 + index));

const CITY_OPTIONS = [
  { value: "all", label: "Semua Kabupaten/Kota" },
  { value: "Kota Pangkalpinang", label: "Kota Pangkalpinang" },
  { value: "Kabupaten Bangka", label: "Kabupaten Bangka" },
  { value: "Kabupaten Bangka Tengah", label: "Kabupaten Bangka Tengah" },
  { value: "Kabupaten Bangka Barat", label: "Kabupaten Bangka Barat" },
  { value: "Kabupaten Bangka Selatan", label: "Kabupaten Bangka Selatan" },
  { value: "Kabupaten Belitung", label: "Kabupaten Belitung" },
  { value: "Kabupaten Belitung Timur", label: "Kabupaten Belitung Timur" },
];

const dateKey = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jakarta" }).format(date);
};

const monthKey = (value) => {
  const key = dateKey(value);
  return key ? String(Number(key.slice(5, 7))) : "";
};

const todayKey = () => dateKey(new Date());

const activeTargetPeriod = (dateFilter, monthFilter) => {
  const source = dateFilter || todayKey();
  const [year, month] = source.split("-");
  return {
    month: monthFilter !== "all" ? Number(monthFilter) : Number(month),
    year: Number(year),
  };
};

const achievementLabel = (actual, target) => {
  if (!target) return "Target belum diset";
  return `${actual}/${target} · ${Math.round((actual / target) * 100)}%`;
};

const percentValue = (actual, target) => {
  if (!target) return null;
  return Math.round((actual / target) * 100);
};

const monthLabel = (value) => MONTH_OPTIONS.find((item) => String(item.value) === String(value))?.label || `Bulan ${value}`;

const formatDateDisplay = (value) => {
  if (!value) return "";
  const [year, month, day] = String(value).split("-");
  if (!year || !month || !day) return "";
  return `${day}/${month}/${year}`;
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" });
};

const EmptyState = ({ message = "Belum ada data" }) => (
  <div className="rounded-xl border border-dashed border-honda-border bg-white p-8 text-center text-sm text-honda-muted">
    {message}
  </div>
);

const initials = (name = "") => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "NA";
  return parts.slice(0, 2).map((part) => part[0]).join("").toUpperCase();
};

const AvatarFallback = ({ name, className = "h-9 w-9 rounded-full", textClassName = "text-xs" }) => (
  <div className={`${className} flex shrink-0 items-center justify-center bg-honda-red-light font-heading font-extrabold text-honda-red ${textClassName}`}>
    {initials(name)}
  </div>
);

const SafeImage = ({ src, alt, fallbackName, className, fallbackClassName, fallbackTextClassName }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return <AvatarFallback name={fallbackName || alt} className={fallbackClassName || className} textClassName={fallbackTextClassName} />;
  }
  return <img src={src} alt={alt} className={className} onError={() => setFailed(true)} />;
};

const productInterest = (item) => (
  item?.interested_product ||
  item?.unit_interest ||
  item?.motor_interest ||
  item?.product_interest ||
  item?.minat_unit ||
  ""
);

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.valid;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold"
      style={{ color: meta.color, background: meta.bg }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: meta.color }} />
      {meta.label}
    </span>
  );
};

// Custom salesman marker for live map
const buildSalesmanIcon = (status, initial) => {
  const colors = {
    visiting: "#22C55E",
    valid: "#22C55E",
    expired: "#E2231A",
    visited: "#22C55E",
    prospect: "#2563EB",
    closing: "#D97706",
    moving: "#3B82F6",
    idle: "#EAB308",
  };
  const color = colors[status] || "#525252";
  return L.divIcon({
    className: "custom-admin-marker",
    html: `
      <div style="
        position:relative;width:34px;height:34px;
      ">
        <span style="
          position:absolute;inset:-6px;border-radius:50%;
          background:${color};opacity:0.25;animation:ping 1.6s cubic-bezier(0,0,0.2,1) infinite;
        "></span>
        <div style="
          position:relative;width:34px;height:34px;border-radius:50%;
          background:${color};border:3px solid #fff;
          box-shadow:0 4px 10px rgba(0,0,0,0.25);
          display:flex;align-items:center;justify-content:center;
          color:#fff;font-weight:800;font-size:12px;font-family:Manrope,sans-serif;
        ">${initial}</div>
      </div>
    `,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  });
};

// ---- Small UI atoms ----
const StatCard = ({ icon: Icon, label, value, sub, trend, accent = "red", testId, onClick }) => {
  const accentMap = {
    red: "from-honda-red to-[#9F140E] text-white",
    dark: "from-honda-ink to-[#2d2d2d] text-white",
    light: "from-white to-honda-bg text-honda-ink border border-honda-border",
    blue: "from-blue-600 to-blue-800 text-white",
  };
  const isLight = accent === "light";
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`relative w-full rounded-2xl p-4 text-left bg-gradient-to-br ${accentMap[accent]} shadow-sm overflow-hidden ${onClick ? "cursor-pointer transition-transform hover:-translate-y-0.5" : "cursor-default"}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p
            className={`text-[10px] uppercase tracking-[0.14em] font-bold ${
              isLight ? "text-honda-muted" : "text-white/70"
            }`}
          >
            {label}
          </p>
          <p className="font-heading font-extrabold text-3xl mt-2 leading-none">{value}</p>
          {sub && (
            <p
              className={`text-[11px] mt-1.5 ${isLight ? "text-honda-muted" : "text-white/75"}`}
            >
              {sub}
            </p>
          )}
        </div>
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            isLight ? "bg-honda-red-light text-honda-red" : "bg-white/15"
          }`}
        >
          <Icon size={20} strokeWidth={2.2} />
        </div>
      </div>
      {trend !== undefined && (
        <div
          className={`mt-3 inline-flex items-center gap-1 text-[11px] font-semibold ${
            isLight ? "text-green-700" : "text-white/95"
          }`}
        >
          <TrendingUp size={11} />
          {trend > 0 ? "+" : ""}
          {trend}% vs minggu lalu
        </div>
      )}
      <div className="absolute -right-6 -bottom-6 w-24 h-24 rounded-full bg-white/5 pointer-events-none" />
    </button>
  );
};

const SidebarLink = ({ icon: Icon, label, active, badge, testId, onClick }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
      active
        ? "bg-honda-red text-white font-semibold shadow shadow-honda-red/30"
        : "text-gray-300 hover:bg-honda-sidebar-hover hover:text-white"
    }`}
  >
    <Icon size={18} strokeWidth={2.1} />
    <span className="flex-1 text-left">{label}</span>
    {badge && (
      <span
        className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
          active ? "bg-white/25 text-white" : "bg-honda-red text-white"
        }`}
      >
        {badge}
      </span>
    )}
  </button>
);

const normalizeRegion = (value) => {
  const source = String(value || "").toLowerCase();
  if (!source) return "";
  if (source.includes("pangkal")) return "Kota Pangkalpinang";
  if (source.includes("bangka tengah")) return "Kabupaten Bangka Tengah";
  if (source.includes("bangka barat")) return "Kabupaten Bangka Barat";
  if (source.includes("bangka selatan") || source.includes("toboali")) return "Kabupaten Bangka Selatan";
  if (source.includes("belitung timur")) return "Kabupaten Belitung Timur";
  if (source.includes("belitung")) return "Kabupaten Belitung";
  if (source.includes("bangka")) return "Kabupaten Bangka";
  return "";
};

const SearchableSelect = ({
  label,
  value,
  onChange,
  options,
  disabled = false,
  placeholder,
  emptyMessage = "Belum ada data",
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const selectedLabel = options.find((option) => option.value === value)?.label || "";
  const [query, setQuery] = useState(selectedLabel);

  useEffect(() => {
    setQuery(selectedLabel);
  }, [selectedLabel]);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setQuery(selectedLabel);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open, selectedLabel]);

  const filteredOptions = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return options;
    return options.filter((option) => option.label.toLowerCase().includes(keyword));
  }, [options, query]);

  return (
    <div ref={containerRef} className="relative min-w-0">
      <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-honda-muted">
        {label}
      </label>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className="flex h-10 w-full items-center justify-between rounded-xl border border-honda-border bg-white px-3 text-left text-sm font-semibold text-honda-ink outline-none transition focus:border-honda-red focus:ring-2 focus:ring-honda-red/20 disabled:cursor-not-allowed disabled:bg-honda-bg disabled:text-honda-muted"
      >
        <span className={selectedLabel ? "truncate" : "truncate text-honda-muted"}>{selectedLabel || placeholder}</span>
        <ChevronDown size={16} className={`shrink-0 text-honda-muted transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && !disabled && (
        <div className="absolute left-0 right-0 top-[calc(100%+0.4rem)] z-[120] overflow-hidden rounded-2xl border border-honda-border bg-white shadow-2xl">
          <div className="border-b border-honda-border p-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-honda-subtle" />
              <input
                autoFocus
                value={query}
                placeholder={placeholder}
                onChange={(event) => setQuery(event.target.value)}
                className="h-10 w-full rounded-xl border border-honda-border bg-honda-bg pl-9 pr-9 text-sm font-medium text-honda-ink outline-none transition focus:border-honda-red focus:ring-2 focus:ring-honda-red/20"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-honda-muted hover:bg-white hover:text-honda-ink"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto py-2">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-3 text-sm text-honda-muted">{emptyMessage}</div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setQuery(option.label);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center px-3 py-2.5 text-left text-sm transition hover:bg-honda-bg ${
                    option.value === value ? "bg-honda-red-light font-semibold text-honda-red" : "text-honda-ink"
                  }`}
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AdminFilterBar = ({
  showDealerFilter,
  dealer,
  setDealer,
  dealerOptions,
  showSalesmanFilter,
  salesman,
  setSalesman,
  salesmanOptions,
  dateFilter,
  setDateFilter,
  monthFilter,
  setMonthFilter,
  cityFilter,
  setCityFilter,
  resetFilters,
  dealerLocked,
}) => (
  <section className="px-6 pt-5">
    <div className="rounded-2xl border border-honda-border bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 pb-3">
        <Filter size={14} className="text-honda-muted" />
        <span className="text-xs font-semibold uppercase tracking-wider text-honda-muted">
          Filter
        </span>
        <span className="ml-auto hidden items-center gap-2 text-[11px] font-semibold text-honda-muted sm:flex">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
          </span>
          Live sync · 30 detik
        </span>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-[1.5fr_1.3fr_0.95fr_1.1fr_auto] xl:items-end">
        {showDealerFilter ? (
          <SearchableSelect
            label="Dealer"
            value={dealer}
            onChange={(next) => {
              setDealer(next);
              setSalesman("all");
            }}
            options={dealerOptions}
            disabled={dealerLocked}
            placeholder="Semua Dealer"
          />
        ) : <div />}
        {showSalesmanFilter ? (
          <SearchableSelect
            label="Salesman"
            value={salesman}
            onChange={setSalesman}
            options={salesmanOptions}
            placeholder="Semua Salesman"
            emptyMessage="Tidak ada sales pada dealer ini"
          />
        ) : <div />}

        <label>
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-honda-muted">
            Bulan
          </span>
          <select
            value={monthFilter}
            onChange={(event) => {
              setMonthFilter(event.target.value);
              if (event.target.value !== "all") setDateFilter("");
              if (event.target.value === "all" && !dateFilter) setDateFilter(todayKey());
            }}
            className="h-10 w-full rounded-xl border border-honda-border bg-white px-3 text-sm font-semibold text-honda-ink outline-none transition focus:border-honda-red focus:ring-2 focus:ring-honda-red/20"
          >
            {MONTH_OPTIONS.map((month) => (
              <option key={month.value} value={month.value}>{month.label}</option>
            ))}
          </select>
        </label>
        <SearchableSelect
          label="Kabupaten/Kota"
          value={cityFilter}
          onChange={setCityFilter}
          options={CITY_OPTIONS}
          placeholder="Semua Kabupaten/Kota"
        />
        <button
          type="button"
          onClick={resetFilters}
          className="h-10 rounded-xl border border-honda-border px-4 text-sm font-bold text-honda-ink transition hover:border-honda-red hover:text-honda-red"
        >
          Reset
        </button>
      </div>
    </div>
  </section>
);

const AdminDetailModal = ({ title, subtitle, image, children, onClose }) => {
  if (!title) return null;
  return (
    <div className="fixed inset-0 z-[900] flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[86vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-honda-border px-5 py-4">
          <div>
            <p className="font-heading text-lg font-extrabold text-honda-ink">{title}</p>
            {subtitle && <p className="mt-1 text-sm text-honda-muted">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-honda-border px-3 py-1.5 text-xs font-bold text-honda-muted hover:border-honda-red hover:text-honda-red"
          >
            Tutup
          </button>
        </div>
        {image && <SafeImage src={image} alt={title} fallbackName={title} className="max-h-[60vh] w-full object-contain bg-honda-ink" fallbackClassName="mx-auto my-8 h-24 w-24 rounded-2xl" fallbackTextClassName="text-2xl" />}
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
};

const DetailGrid = ({ rows }) => (
  <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
    {rows.map(([label, value]) => (
      <div key={label} className="rounded-xl border border-honda-border bg-honda-bg p-3">
        <p className="text-[10px] font-bold uppercase tracking-wider text-honda-muted">{label}</p>
        <p className="mt-1 font-semibold text-honda-ink">{value || "—"}</p>
      </div>
    ))}
  </div>
);

const AdminSectionHeader = ({ title, subtitle, children }) => (
  <div className="flex flex-col gap-3 border-b border-honda-border px-5 py-4 md:flex-row md:items-center md:justify-between">
    <div>
      <p className="font-heading text-base font-extrabold leading-none text-honda-ink">{title}</p>
      {subtitle && <p className="mt-1.5 text-[11px] text-honda-muted">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const AdminMapPanel = ({ title, subtitle, rows, mapLayer, setMapLayer, coverageFilter, setCoverageFilter, showHeatmap = true, hotspots = [] }) => (
  <div className="honda-card overflow-hidden">
    <AdminSectionHeader title={title} subtitle={subtitle}>
      {showHeatmap && (
        <div className="flex rounded-full bg-honda-bg p-1">
          <button
            onClick={() => setMapLayer("live")}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold ${
              mapLayer === "live" ? "bg-honda-red text-white shadow" : "text-honda-muted"
            }`}
          >
            <Radio size={11} /> Live
          </button>
          <button
            onClick={() => setMapLayer("heatmap")}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold ${
              mapLayer === "heatmap" ? "bg-honda-red text-white shadow" : "text-honda-muted"
            }`}
          >
            <Layers size={11} /> Heatmap
          </button>
        </div>
      )}
    </AdminSectionHeader>
    <div className="flex flex-wrap gap-2 border-b border-honda-border px-5 py-3">
      {[
        ["all", "Semua"],
        ["valid", "Valid"],
        ["expired", "Expired"],
        ["prospect", "Prospek"],
        ["closing", "Closing"],
      ].map(([value, label]) => (
        <button
          key={value}
          onClick={() => setCoverageFilter(value)}
          className={`rounded-full px-3 py-1.5 text-[11px] font-semibold ${
            coverageFilter === value ? "bg-honda-red text-white" : "bg-honda-bg text-honda-muted"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
    <div className="h-[520px] w-full">
      <MapContainer center={[HOME_COORD.lat, HOME_COORD.lng]} zoom={10} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
        <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {mapLayer === "live" &&
          rows.filter((s) => s.status !== "out_of_area").map((s) => (
            <Marker key={s.id} position={[s.coord.lat, s.coord.lng]} icon={buildSalesmanIcon(s.status, s.name?.[0] || "S")}>
              <Popup>
                <div className="min-w-[170px]">
                  <b>{s.name}</b>
                  <p className="mt-1 text-[11px] text-honda-muted">{s.dealer}</p>
                  <p className="mt-1 text-[11px]">Status: <StatusPill status={s.status} /></p>
                  {s.photoUrl && (
                    <a href={s.photoUrl} target="_blank" rel="noreferrer">
                      <SafeImage src={s.photoUrl} alt="Foto evidence check-in" fallbackName={s.name} className="mt-2 h-24 w-full rounded-lg object-cover" fallbackClassName="mt-2 h-24 w-full rounded-lg" />
                    </a>
                  )}
                  <p className="mt-1 text-[11px]">Accuracy GPS: {Math.round(s.accuracy || 0)}m</p>
                  <p className="mt-1 font-mono text-[11px]">
                    {Number(s.coord.lat).toFixed(6)}, {Number(s.coord.lng).toFixed(6)}
                  </p>
                  <p className="mt-1 text-[11px] text-honda-muted">{s.lastUpdate}</p>
                </div>
              </Popup>
            </Marker>
          ))}
        {mapLayer === "heatmap" &&
          hotspots.map((h, idx) => (
            <Circle
              key={idx}
              center={[h.lat, h.lng]}
              radius={2500 + h.intensity * 5000}
              pathOptions={{ color: "#E2231A", fillColor: "#E2231A", fillOpacity: 0.2 + h.intensity * 0.25, weight: 1 }}
            />
          ))}
      </MapContainer>
    </div>
  </div>
);

const AdminRouteContent = ({
  activeNav,
  filteredSalesmen,
  salesmanRows,
  prospects,
  evidenceRows,
  dealerRows,
  auditRows,
  coverageStats,
  coverageSetting,
  settingDraft,
  setSettingDraft,
  saveCoverageSetting,
  babelTarget,
  babelTargetDraft,
  setBabelTargetDraft,
  saveBabelTarget,
  user,
  mapLayer,
  setMapLayer,
  coverageFilter,
  setCoverageFilter,
  hotspots,
  scorecard,
  productDistribution,
  settingsTab,
  setSettingsTab,
  dealerTargets,
  salesTargets,
  dealerTargetDraft,
  setDealerTargetDraft,
  salesTargetDraft,
  setSalesTargetDraft,
  saveDealerTarget,
  saveSalesTarget,
  deleteDealerTarget,
  deleteSalesTarget,
  canManageTargets,
  targetSalesmanOptions,
  dealerOptions,
  dealerLocked,
  onOpenSalesman,
  onOpenProspect,
  onOpenEvidence,
  managedUsers,
  manageableRoles,
  userRoleFilter,
  setUserRoleFilter,
  userDealerFilter,
  setUserDealerFilter,
  userStatusFilter,
  setUserStatusFilter,
  userSearch,
  setUserSearch,
  openCreateUser,
  openEditUser,
  toggleManagedUser,
  setResetTarget,
  branchHeadOptions,
  teamLeaderOptions,
}) => {
  if (activeNav === "live-map") {
    return (
      <section className="px-6 py-5">
        <AdminMapPanel
          title="Live Map Aktivitas Sales"
          subtitle="Marker kunjungan valid, prospek, dan closing dengan filter dealer, sales, tanggal, dan status."
          rows={filteredSalesmen}
          mapLayer={mapLayer}
          setMapLayer={setMapLayer}
          coverageFilter={coverageFilter}
          setCoverageFilter={setCoverageFilter}
          hotspots={hotspots}
        />
      </section>
    );
  }

  if (activeNav === "salesman") {
    return (
      <section className="px-6 py-5">
        <div className="honda-card overflow-hidden">
          <AdminSectionHeader title="Daftar Salesman" subtitle="Status aktif, target per sales, aktual kunjungan/prospek/closing, gap target, dan check-in terakhir." />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-honda-bg text-left text-[10px] uppercase tracking-wider text-honda-muted">
                <tr>
                  {["Salesman", "Dealer", "Status", "Target", "Aktual", "Gap", "Last Check-in", ""].map((h) => <th key={h} className="px-5 py-3 font-bold">{h}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-honda-border">
                {salesmanRows.length === 0 ? (
                  <tr><td colSpan={8} className="py-10 text-center text-honda-muted">Belum ada data</td></tr>
                ) : salesmanRows.map((s) => (
                  <tr key={s.id} className="hover:bg-honda-bg">
                    <td className="px-5 py-3">
                      <button type="button" onClick={() => onOpenSalesman(s)} className="font-semibold text-honda-ink hover:text-honda-red">
                        {s.name}
                      </button>
                    </td>
                    <td className="px-5 py-3 text-honda-muted">{s.dealer}</td>
                    <td className="px-5 py-3"><StatusPill status={s.active ? "valid" : "expired"} /></td>
                    <td className="px-5 py-3 text-xs text-honda-muted">
                      {s.targetVisits || s.targetProspects || s.targetClosing ? `${s.targetVisits}/${s.targetProspects}/${s.targetClosing}` : "Belum diset"}
                    </td>
                    <td className="px-5 py-3 text-xs"><b>{s.visits}/{s.prospects}/{s.closing}</b></td>
                    <td className="px-5 py-3 text-xs text-honda-muted">{s.gapVisits}/{s.gapProspects}/{s.gapClosing}</td>
                    <td className="px-5 py-3 text-honda-muted">{s.lastCheckin}</td>
                    <td className="px-5 py-3 text-right"><button onClick={() => onOpenSalesman(s)} className="text-xs font-bold text-honda-red">Detail</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    );
  }

  if (activeNav === "users") {
    const counts = {
      total: managedUsers.length,
      main_dealer: managedUsers.filter((row) => row.role === "main_dealer").length,
      kepala_cabang: managedUsers.filter((row) => row.role === "kepala_cabang").length,
      team_leader: managedUsers.filter((row) => row.role === "team_leader").length,
      sales: managedUsers.filter((row) => row.role === "sales" && row.active !== false).length,
    };
    return (
      <section className="px-6 py-5">
        <div className="honda-card overflow-hidden">
          <AdminSectionHeader title="Kelola User" subtitle="Atur akun, role, dealer, kepala cabang, team leader, status akun, dan reset password.">
            {user?.role !== "sales" && (
              <button onClick={openCreateUser} className="rounded-xl bg-honda-red px-4 py-2 text-sm font-bold text-white">
                + Tambah User
              </button>
            )}
          </AdminSectionHeader>

          <div className="grid grid-cols-2 gap-3 border-b border-honda-border p-5 lg:grid-cols-5">
            {[
              ["Total User", counts.total],
              ["Main Dealer", counts.main_dealer],
              ["Kepala Cabang", counts.kepala_cabang],
              ["Team Leader", counts.team_leader],
              ["Sales Aktif", counts.sales],
            ].map(([label, value]) => (
              <div key={label} className="rounded-xl border border-honda-border bg-honda-bg p-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-honda-muted">{label}</p>
                <p className="mt-1 font-heading text-2xl font-extrabold text-honda-ink">{value}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-3 border-b border-honda-border p-5 md:grid-cols-4">
            <input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Cari nama/email/HP..." className="h-10 rounded-xl border border-honda-border px-3 text-sm" />
            <select value={userRoleFilter} onChange={(e) => setUserRoleFilter(e.target.value)} className="h-10 rounded-xl border border-honda-border bg-white px-3 text-sm">
              <option value="all">Semua Role</option>
              {manageableRoles.map((role) => <option key={role} value={role}>{ROLE_LABELS[role]}</option>)}
            </select>
            <select value={userDealerFilter} onChange={(e) => setUserDealerFilter(e.target.value)} className="h-10 rounded-xl border border-honda-border bg-white px-3 text-sm">
              {dealerOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
            <select value={userStatusFilter} onChange={(e) => setUserStatusFilter(e.target.value)} className="h-10 rounded-xl border border-honda-border bg-white px-3 text-sm">
              <option value="all">Semua Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-honda-bg text-left text-[10px] uppercase tracking-wider text-honda-muted">
                <tr>{["Nama", "Email", "Role", "Dealer", "Kepala Cabang", "Team Leader", "Status", "Last Login", "Action"].map((h) => <th key={h} className="px-4 py-3 font-bold">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-honda-border">
                {managedUsers.length === 0 ? (
                  <tr><td colSpan={9} className="py-10 text-center text-honda-muted">Belum ada user</td></tr>
                ) : managedUsers.map((row) => (
                  <tr key={row.id} className="hover:bg-honda-bg">
                    <td className="px-4 py-3 font-semibold text-honda-ink">{row.name}</td>
                    <td className="px-4 py-3 text-honda-muted">{row.email}</td>
                    <td className="px-4 py-3">{ROLE_LABELS[row.role] || row.role}</td>
                    <td className="px-4 py-3">{row.dealer || "—"}</td>
                    <td className="px-4 py-3">{row.branch_head_name || "—"}</td>
                    <td className="px-4 py-3">{row.team_leader_name || "—"}</td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${row.active !== false ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>{row.active !== false ? "Active" : "Inactive"}</span></td>
                    <td className="px-4 py-3 text-honda-muted">{formatDateTime(row.last_login)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2 text-xs font-bold">
                        <button onClick={() => openEditUser(row)} className="text-honda-red">Edit</button>
                        {user?.role === "main_dealer" && <button onClick={() => setResetTarget(row)} className="text-blue-600">Reset Password</button>}
                        {user?.role === "main_dealer" && <button onClick={() => setDeleteUserTarget(row)} className="text-honda-red">Hapus</button>}
                        <button onClick={() => toggleManagedUser(row).catch(() => toast.error("Gagal mengubah status user."))} className="text-honda-muted">{row.active === false ? "Aktifkan" : "Nonaktifkan"}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    );
  }

  if (activeNav === "prospek") {
    return (
      <section className="px-6 py-5">
        <div className="honda-card overflow-hidden">
          <AdminSectionHeader title="Tabel Prospek" subtitle="Nama prospek, no HP, minat unit, sales, dealer, status follow-up, dan tanggal input." />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-honda-bg text-left text-[10px] uppercase tracking-wider text-honda-muted">
                <tr>
                  {["Nama", "No HP", "Minat Unit", "Sales", "Dealer", "Status", "Tanggal Input"].map((h) => <th key={h} className="px-5 py-3 font-bold">{h}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-honda-border">
                {prospects.length === 0 ? (
                  <tr><td colSpan={7} className="py-10 text-center text-honda-muted">Belum ada data</td></tr>
                ) : prospects.map((p) => (
                  <tr key={p.id} className="hover:bg-honda-bg">
                    <td className="px-5 py-3">
                      <button type="button" onClick={() => onOpenProspect(p)} className="font-semibold text-honda-ink hover:text-honda-red">
                        {p.name || "—"}
                      </button>
                    </td>
                    <td className="px-5 py-3 text-honda-muted">{p.phone || "—"}</td>
                    <td className="px-5 py-3">{productInterest(p) || "—"}</td>
                    <td className="px-5 py-3">{p.salesman_name || "—"}</td>
                    <td className="px-5 py-3">{resolveDealerMeta(p.dealer_id, p.dealer_name || p.dealer_id)?.name || "Dealer tidak ditemukan"}</td>
                    <td className="px-5 py-3"><StatusPill status={p.status === "closing" ? "closing" : "prospect"} /></td>
                    <td className="px-5 py-3 text-honda-muted">{formatDateTime(p.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    );
  }

  if (activeNav === "bukti-foto") {
    return (
      <section className="px-6 py-5">
        <div className="honda-card p-5">
          <AdminSectionHeader title="Gallery Evidence Check-in" subtitle="Foto live, sales, dealer, lokasi, jam check-in, dan status valid/prospek/closing." />
          {evidenceRows.length === 0 ? <EmptyState /> : (
            <div className="grid grid-cols-1 gap-4 pt-5 sm:grid-cols-2 xl:grid-cols-4">
              {evidenceRows.map((e) => (
                <button key={e.id} type="button" onClick={() => onOpenEvidence(e)} className="overflow-hidden rounded-xl border border-honda-border bg-white text-left">
                  <SafeImage src={e.photo_url} alt={e.salesman_name || "Bukti foto"} fallbackName={e.salesman_name} className="h-44 w-full object-cover" fallbackClassName="h-44 w-full" />
                  <div className="space-y-1 p-3 text-xs text-honda-muted">
                    <p className="font-semibold text-honda-ink">{e.salesman_name || "—"}</p>
                    <p>{e.dealer_id || "—"} · {e.address || "Lokasi tersimpan"}</p>
                    <p>{formatDateTime(e.checkin_time)}</p>
                    <StatusPill status={e.visual_status || e.status} />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
    );
  }

  if (activeNav === "analytics") {
    return (
      <section className="grid grid-cols-1 gap-4 px-6 py-5 xl:grid-cols-3">
        <div className="honda-card p-5">
          <p className="font-heading font-extrabold text-honda-ink">Babel Coverage</p>
          <p className="mt-3 text-4xl font-extrabold text-honda-red">{coverageStats?.target_set ? `${coverageStats?.coverage_score ?? 0}%` : "—"}</p>
          <p className="mt-1 text-xs text-honda-muted">
            {coverageStats?.target_set ? `${coverageStats?.area_valid ?? 0} dari ${coverageStats?.target_area ?? 0} kabupaten/kota` : "Target Babel belum diset"}
          </p>
          <p className="mt-1 text-xs text-honda-muted">
            Belum dikunjungi: {coverageStats?.area_unvisited ?? 0} · Revisit: {coverageStats?.area_need_revisit ?? 0}
          </p>
        </div>
        <div className="honda-card p-5 xl:col-span-2">
          <p className="font-heading font-extrabold text-honda-ink">Dealer Performance</p>
          <div className="mt-4 space-y-3">
            {scorecard.length === 0 ? <EmptyState /> : scorecard.map((c, i) => (
              <div key={`${c.dealer}-${i}`}>
                <div className="mb-1 flex justify-between text-sm"><span>{c.dealer}</span><b>{c.closing} closing / {c.prospects} prospek</b></div>
                <div className="h-2 rounded-full bg-honda-bg"><div className="h-2 rounded-full bg-honda-red" style={{ width: `${c.prospects ? Math.round((c.closing / c.prospects) * 100) : 0}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
        <div className="honda-card p-5 xl:col-span-3">
          <p className="font-heading font-extrabold text-honda-ink">Trend Kunjungan Harian/Mingguan/Bulanan</p>
          <div className="mt-4 h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={productDistribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <ChartTooltip />
                <Line type="monotone" dataKey="value" stroke="#E2231A" strokeWidth={3} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>
    );
  }

  if (activeNav === "coverage-map") {
    return (
      <section className="px-6 py-5">
        <AdminMapPanel
          title="Coverage Map"
          subtitle="Hijau valid, biru prospek, emas closing, merah expired/belum dikunjungi. Heatmap dapat diaktifkan."
          rows={filteredSalesmen}
          mapLayer={mapLayer}
          setMapLayer={setMapLayer}
          coverageFilter={coverageFilter}
          setCoverageFilter={setCoverageFilter}
          hotspots={hotspots}
        />
      </section>
    );
  }

  if (activeNav === "dealer") {
    return (
      <section className="px-6 py-5">
        <div className="honda-card overflow-hidden">
          <AdminSectionHeader title="Daftar Dealer" subtitle="Target dealer, aktual kunjungan/prospek/closing, achievement, dan status aktif." />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-honda-bg text-left text-[10px] uppercase tracking-wider text-honda-muted">
                <tr>{["Dealer", "Sales", "Target", "Aktual", "Achievement", "Status"].map((h) => <th key={h} className="px-5 py-3 font-bold">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-honda-border">
                {dealerRows.length === 0 ? <tr><td colSpan={6} className="py-10 text-center text-honda-muted">Belum ada data</td></tr> : dealerRows.map((d) => (
                  <tr key={d.id} className="hover:bg-honda-bg">
                    <td className="px-5 py-3 font-semibold text-honda-ink">{d.name}</td>
                    <td className="px-5 py-3">{d.sales}</td>
                    <td className="px-5 py-3 text-xs text-honda-muted">
                      {d.targetVisits || d.targetProspects || d.targetClosing ? `${d.targetVisits}/${d.targetProspects}/${d.targetClosing}` : "Belum diset"}
                    </td>
                    <td className="px-5 py-3 text-xs"><b>{d.visits}/{d.prospects}/{d.closing}</b></td>
                    <td className="px-5 py-3 text-xs text-honda-muted">{d.coverageAchievement}</td>
                    <td className="px-5 py-3"><StatusPill status="valid" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    );
  }

  if (activeNav === "audit-log") {
    return (
      <section className="px-6 py-5">
        <div className="honda-card overflow-hidden">
          <AdminSectionHeader title="Audit Log" subtitle="Riwayat login, check-in, update prospek, dan update coverage setting." />
          <div className="divide-y divide-honda-border">
            {auditRows.length === 0 ? <div className="p-5"><EmptyState /></div> : auditRows.map((row) => (
              <div key={row.id} className="flex flex-col gap-1 px-5 py-3 text-sm md:flex-row md:items-center md:justify-between">
                <div><b className="text-honda-ink">{row.action}</b><p className="text-xs text-honda-muted">{row.user} · {row.detail}</p></div>
                <span className="text-xs text-honda-muted">{row.time}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (activeNav === "settings") {
    return (
      <section className="px-6 py-5">
        <div className="honda-card p-5">
          <p className="font-heading text-lg font-extrabold text-honda-ink">Settings</p>
          <p className="mt-1 text-sm text-honda-muted">Kelola validity coverage dan target bulanan dealer maupun sales.</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {[
              ["validity", "Coverage Validity"],
              ["babel", "Target Babel Coverage"],
              ["dealer", "Target Dealer"],
              ["sales", "Target Sales"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setSettingsTab(key)}
                className={`rounded-full px-4 py-2 text-sm font-semibold ${settingsTab === key ? "bg-honda-red text-white" : "bg-honda-bg text-honda-muted"}`}
              >
                {label}
              </button>
            ))}
          </div>

          {settingsTab === "validity" && (
            <div className="mt-5 rounded-xl border border-honda-border p-4">
              <label className="text-xs font-bold uppercase tracking-wider text-honda-muted">Coverage Validity Days</label>
              <div className="mt-3 flex flex-wrap gap-2">
                {[7, 14, 30, 45, 60].map((days) => (
                  <button
                    key={days}
                    type="button"
                    disabled={user?.role !== "main_dealer"}
                    onClick={() => setSettingDraft(String(days))}
                    className={`rounded-full px-3 py-1.5 text-xs font-bold ${Number(settingDraft) === days ? "bg-honda-red text-white" : "bg-honda-bg text-honda-muted"}`}
                  >
                    {days} hari
                  </button>
                ))}
              </div>
              <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={settingDraft}
                  onChange={(e) => setSettingDraft(e.target.value)}
                  disabled={user?.role !== "main_dealer"}
                  className="rounded-xl border border-honda-border px-3 py-2 text-sm"
                />
                <button
                  onClick={() => saveCoverageSetting().catch(() => {})}
                  disabled={user?.role !== "main_dealer"}
                  className="rounded-xl bg-honda-red px-4 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Simpan Setting
                </button>
              </div>
              {user?.role !== "main_dealer" && <p className="mt-2 text-xs text-honda-muted">Hanya Main Dealer yang bisa mengubah setting ini.</p>}
            </div>
          )}

          {settingsTab === "babel" && (
            <div className="mt-5 rounded-xl border border-honda-border p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-honda-muted">Target Babel Coverage</p>
              <p className="mt-1 text-sm text-honda-muted">
                {babelTarget?.id ? `Target aktif: ${babelTarget.target_area} wilayah, ${babelTarget.target_checkin} check-in, ${babelTarget.target_prospect} prospek, ${babelTarget.target_closing} closing` : "Target Babel belum diset"}
              </p>
              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
                <label>
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-honda-muted">Bulan</span>
                  <select
                    value={babelTargetDraft.month}
                    disabled={user?.role !== "main_dealer"}
                    onChange={(e) => setBabelTargetDraft((current) => ({ ...current, month: e.target.value }))}
                    className="h-10 w-full rounded-xl border border-honda-border bg-white px-3 text-sm"
                  >
                    {MONTH_OPTIONS.filter((item) => item.value !== "all").map((month) => <option key={month.value} value={month.value}>{month.label}</option>)}
                  </select>
                </label>
                <label>
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-honda-muted">Tahun</span>
                  <select
                    value={babelTargetDraft.year}
                    disabled={user?.role !== "main_dealer"}
                    onChange={(e) => setBabelTargetDraft((current) => ({ ...current, year: e.target.value }))}
                    className="h-10 w-full rounded-xl border border-honda-border bg-white px-3 text-sm"
                  >
                    {TARGET_YEAR_OPTIONS.map((year) => <option key={year} value={year}>{year}</option>)}
                  </select>
                </label>
                <label>
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-honda-muted">Target Area Babel</span>
                  <input
                    type="number"
                    min="1"
                    max="7"
                    step="1"
                    value={babelTargetDraft.target_area}
                    disabled={user?.role !== "main_dealer"}
                    onChange={(e) => setBabelTargetDraft((current) => ({ ...current, target_area: e.target.value }))}
                    className="h-10 w-full rounded-xl border border-honda-border px-3 text-sm"
                  />
                </label>
                <label>
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-honda-muted">Target Check-In</span>
                  <input type="number" min="0" step="1" value={babelTargetDraft.target_checkin} disabled={user?.role !== "main_dealer"} onChange={(e) => setBabelTargetDraft((current) => ({ ...current, target_checkin: e.target.value }))} className="h-10 w-full rounded-xl border border-honda-border px-3 text-sm" />
                </label>
                <label>
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-honda-muted">Target Prospek</span>
                  <input type="number" min="0" step="1" value={babelTargetDraft.target_prospect} disabled={user?.role !== "main_dealer"} onChange={(e) => setBabelTargetDraft((current) => ({ ...current, target_prospect: e.target.value }))} className="h-10 w-full rounded-xl border border-honda-border px-3 text-sm" />
                </label>
                <label>
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-honda-muted">Target Closing</span>
                  <input type="number" min="0" step="1" value={babelTargetDraft.target_closing} disabled={user?.role !== "main_dealer"} onChange={(e) => setBabelTargetDraft((current) => ({ ...current, target_closing: e.target.value }))} className="h-10 w-full rounded-xl border border-honda-border px-3 text-sm" />
                </label>
              </div>
              <button
                onClick={() => saveBabelTarget().catch(() => {})}
                disabled={user?.role !== "main_dealer"}
                className="mt-4 rounded-xl bg-honda-red px-4 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Simpan Target Babel
              </button>
              {user?.role !== "main_dealer" && <p className="mt-2 text-xs text-honda-muted">Hanya Main Dealer yang bisa mengubah target ini.</p>}
            </div>
          )}

          {settingsTab === "dealer" && (
            <div className="mt-5 space-y-5">
              <div className="rounded-xl border border-honda-border p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-honda-muted">Target Dealer</p>
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
                  <SearchableSelect label="Dealer" value={dealerTargetDraft.dealer_id} onChange={(value) => setDealerTargetDraft((current) => ({ ...current, dealer_id: value }))} options={dealerOptions.filter((item) => item.value !== "all")} disabled={!canManageTargets || dealerLocked} placeholder="Pilih Dealer" />
                  <label><span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-honda-muted">Bulan</span><select value={dealerTargetDraft.month} disabled={!canManageTargets} onChange={(e) => setDealerTargetDraft((current) => ({ ...current, month: e.target.value }))} className="h-10 w-full rounded-xl border border-honda-border bg-white px-3 text-sm">{MONTH_OPTIONS.filter((item) => item.value !== "all").map((month) => <option key={month.value} value={month.value}>{month.label}</option>)}</select></label>
                  <label><span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-honda-muted">Tahun</span><select value={dealerTargetDraft.year} disabled={!canManageTargets} onChange={(e) => setDealerTargetDraft((current) => ({ ...current, year: e.target.value }))} className="h-10 w-full rounded-xl border border-honda-border bg-white px-3 text-sm">{TARGET_YEAR_OPTIONS.map((year) => <option key={year} value={year}>{year}</option>)}</select></label>
                  <label><span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-honda-muted">Target Area</span><input type="number" min="0" max="7" step="1" value={dealerTargetDraft.target_area} disabled={!canManageTargets} onChange={(e) => setDealerTargetDraft((current) => ({ ...current, target_area: e.target.value }))} className="h-10 w-full rounded-xl border border-honda-border px-3 text-sm" /></label>
                  <label><span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-honda-muted">Target Check-in</span><input type="number" min="0" step="1" value={dealerTargetDraft.target_checkin} disabled={!canManageTargets} onChange={(e) => setDealerTargetDraft((current) => ({ ...current, target_checkin: e.target.value }))} className="h-10 w-full rounded-xl border border-honda-border px-3 text-sm" /></label>
                  <label><span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-honda-muted">Target Prospek</span><input type="number" min="0" step="1" value={dealerTargetDraft.target_prospect} disabled={!canManageTargets} onChange={(e) => setDealerTargetDraft((current) => ({ ...current, target_prospect: e.target.value }))} className="h-10 w-full rounded-xl border border-honda-border px-3 text-sm" /></label>
                  <label><span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-honda-muted">Target Closing</span><input type="number" min="0" step="1" value={dealerTargetDraft.target_closing} disabled={!canManageTargets} onChange={(e) => setDealerTargetDraft((current) => ({ ...current, target_closing: e.target.value }))} className="h-10 w-full rounded-xl border border-honda-border px-3 text-sm" /></label>
                </div>
                {canManageTargets && <div className="mt-4"><button onClick={() => saveDealerTarget().catch(() => {})} className="rounded-xl bg-honda-red px-4 py-2 text-sm font-bold text-white">Simpan Target Dealer</button></div>}
              </div>
              <div className="overflow-x-auto rounded-xl border border-honda-border">
                <table className="w-full text-sm">
                  <thead className="bg-honda-bg text-left text-[10px] uppercase tracking-wider text-honda-muted">
                    <tr>{["Dealer", "Bulan", "Target Area", "Target Check-in", "Target Prospek", "Target Closing", "Action"].map((h) => <th key={h} className="px-4 py-3 font-bold">{h}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-honda-border">
                    {dealerTargets.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-honda-muted">Belum ada data</td></tr> : dealerTargets.map((row) => (
                      <tr key={row.id}>
                        <td className="px-4 py-3 font-semibold text-honda-ink">{row.dealer_name}</td>
                        <td className="px-4 py-3 text-honda-muted">{monthLabel(row.month)} {row.year}</td>
                        <td className="px-4 py-3">{row.target_area}</td>
                        <td className="px-4 py-3">{row.target_checkin}</td>
                        <td className="px-4 py-3">{row.target_prospect}</td>
                        <td className="px-4 py-3">{row.target_closing}</td>
                        <td className="px-4 py-3">{canManageTargets ? <div className="flex gap-3 text-xs font-bold"><button onClick={() => setDealerTargetDraft({ id: row.id, dealer_id: row.dealer_id, month: String(row.month), year: String(row.year), target_area: String(row.target_area), target_checkin: String(row.target_checkin), target_prospect: String(row.target_prospect), target_closing: String(row.target_closing) })} className="text-honda-red">Edit</button><button onClick={() => deleteDealerTarget(row.id).catch(() => {})} className="text-honda-muted">Delete</button></div> : <span className="text-xs text-honda-muted">View</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {settingsTab === "sales" && (
            <div className="mt-5 space-y-5">
              <div className="rounded-xl border border-honda-border p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-honda-muted">Target Sales</p>
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-7">
                  <SearchableSelect label="Dealer" value={salesTargetDraft.dealer_id} onChange={(value) => setSalesTargetDraft((current) => ({ ...current, dealer_id: value, salesman_id: "all" }))} options={dealerOptions.filter((item) => item.value !== "all")} disabled={!canManageTargets || dealerLocked} placeholder="Pilih Dealer" />
                  <SearchableSelect label="Salesman" value={salesTargetDraft.salesman_id} onChange={(value) => setSalesTargetDraft((current) => ({ ...current, salesman_id: value }))} options={targetSalesmanOptions} disabled={!canManageTargets} placeholder="Pilih Sales" />
                  <label><span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-honda-muted">Bulan</span><select value={salesTargetDraft.month} disabled={!canManageTargets} onChange={(e) => setSalesTargetDraft((current) => ({ ...current, month: e.target.value }))} className="h-10 w-full rounded-xl border border-honda-border bg-white px-3 text-sm">{MONTH_OPTIONS.filter((item) => item.value !== "all").map((month) => <option key={month.value} value={month.value}>{month.label}</option>)}</select></label>
                  <label><span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-honda-muted">Tahun</span><select value={salesTargetDraft.year} disabled={!canManageTargets} onChange={(e) => setSalesTargetDraft((current) => ({ ...current, year: e.target.value }))} className="h-10 w-full rounded-xl border border-honda-border bg-white px-3 text-sm">{TARGET_YEAR_OPTIONS.map((year) => <option key={year} value={year}>{year}</option>)}</select></label>
                  <label><span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-honda-muted">Target Area</span><input type="number" min="0" max="7" step="1" value={salesTargetDraft.target_area} disabled={!canManageTargets} onChange={(e) => setSalesTargetDraft((current) => ({ ...current, target_area: e.target.value }))} className="h-10 w-full rounded-xl border border-honda-border px-3 text-sm" /></label>
                  <label><span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-honda-muted">Target Check-in</span><input type="number" min="0" step="1" value={salesTargetDraft.target_checkin} disabled={!canManageTargets} onChange={(e) => setSalesTargetDraft((current) => ({ ...current, target_checkin: e.target.value }))} className="h-10 w-full rounded-xl border border-honda-border px-3 text-sm" /></label>
                  <label><span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-honda-muted">Target Prospek</span><input type="number" min="0" step="1" value={salesTargetDraft.target_prospect} disabled={!canManageTargets} onChange={(e) => setSalesTargetDraft((current) => ({ ...current, target_prospect: e.target.value }))} className="h-10 w-full rounded-xl border border-honda-border px-3 text-sm" /></label>
                  <label><span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-honda-muted">Target Closing</span><input type="number" min="0" step="1" value={salesTargetDraft.target_closing} disabled={!canManageTargets} onChange={(e) => setSalesTargetDraft((current) => ({ ...current, target_closing: e.target.value }))} className="h-10 w-full rounded-xl border border-honda-border px-3 text-sm" /></label>
                </div>
                {canManageTargets && <div className="mt-4"><button onClick={() => saveSalesTarget().catch(() => {})} className="rounded-xl bg-honda-red px-4 py-2 text-sm font-bold text-white">Simpan Target Sales</button></div>}
              </div>
              <div className="overflow-x-auto rounded-xl border border-honda-border">
                <table className="w-full text-sm">
                  <thead className="bg-honda-bg text-left text-[10px] uppercase tracking-wider text-honda-muted">
                    <tr>{["Salesman", "Dealer", "Bulan", "Target Area", "Target Check-in", "Target Prospek", "Target Closing", "Action"].map((h) => <th key={h} className="px-4 py-3 font-bold">{h}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-honda-border">
                    {salesTargets.length === 0 ? <tr><td colSpan={7} className="px-4 py-8 text-center text-honda-muted">Belum ada data</td></tr> : salesTargets.map((row) => (
                      <tr key={row.id}>
                        <td className="px-4 py-3 font-semibold text-honda-ink">{row.salesman_name}</td>
                        <td className="px-4 py-3 text-honda-muted">{row.dealer_name}</td>
                        <td className="px-4 py-3 text-honda-muted">{monthLabel(row.month)} {row.year}</td>
                        <td className="px-4 py-3">{row.target_area}</td>
                        <td className="px-4 py-3">{row.target_checkin}</td>
                        <td className="px-4 py-3">{row.target_prospect}</td>
                        <td className="px-4 py-3">{row.target_closing}</td>
                        <td className="px-4 py-3">{canManageTargets ? <div className="flex gap-3 text-xs font-bold"><button onClick={() => setSalesTargetDraft({ id: row.id, dealer_id: row.dealer_id, salesman_id: row.salesman_id, month: String(row.month), year: String(row.year), target_area: String(row.target_area), target_checkin: String(row.target_checkin), target_prospect: String(row.target_prospect), target_closing: String(row.target_closing) })} className="text-honda-red">Edit</button><button onClick={() => deleteSalesTarget(row.id).catch(() => {})} className="text-honda-muted">Delete</button></div> : <span className="text-xs text-honda-muted">View</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>
    );
  }

  return null;
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const dealerLocked = user?.role === "team_leader" || user?.role === "kepala_cabang";
  const [dealer, setDealer] = useState(dealerLocked ? user?.dealer_id || "all" : "all");
  const [salesman, setSalesman] = useState("all");
  const [search, setSearch] = useState("");
  const [mapLayer, setMapLayer] = useState("live"); // live | heatmap
  const [coverageFilter, setCoverageFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");

  // KPI dari real API
  const [kpi, setKpi] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [scorecard, setScorecard] = useState([]);
  const [dealers, setDealers] = useState([{ id: "all", name: "Semua Dealer" }]);
  const [liveMap, setLiveMap] = useState([]);
  const [heatmap, setHeatmap] = useState([]);
  const [coverageStats, setCoverageStats] = useState(null);
  const [coverageSetting, setCoverageSetting] = useState({ coverage_valid_days: 30 });
  const [settingDraft, setSettingDraft] = useState(30);
  const [babelTarget, setBabelTarget] = useState({ target_set: false, target_area: null });
  const [babelTargetDraft, setBabelTargetDraft] = useState({
    month: String(new Date().getMonth() + 1),
    year: String(new Date().getFullYear()),
    target_area: "",
    target_area: "",
    target_checkin: "",
    target_prospect: "",
    target_closing: "",
  });
  const [settingsTab, setSettingsTab] = useState("validity");
  const [users, setUsers] = useState([]);
  const [userRoleFilter, setUserRoleFilter] = useState("all");
  const [userDealerFilter, setUserDealerFilter] = useState("all");
  const [userStatusFilter, setUserStatusFilter] = useState("all");
  const [userSearch, setUserSearch] = useState("");
  const [userModal, setUserModal] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPasswordDraft, setResetPasswordDraft] = useState("");
  const emptyUserDraft = {
    name: "",
    email: "",
    password: "",
    phone: "",
    role: "sales",
    dealer_id: dealerLocked ? user?.dealer_id || "" : "",
    branch_head_id: "",
    team_leader_id: "",
    active: true,
  };
  const [userDraft, setUserDraft] = useState(emptyUserDraft);
  const [deleteUserTarget, setDeleteUserTarget] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [dealerTargets, setDealerTargets] = useState([]);
  const [salesTargets, setSalesTargets] = useState([]);
  const [targetSettings, setTargetSettings] = useState([]);
  const [dealerTargetDraft, setDealerTargetDraft] = useState({
    id: null,
    dealer_id: dealerLocked ? user?.dealer_id || "all" : "all",
    month: String(new Date().getMonth() + 1),
    year: String(new Date().getFullYear()),
    target_area: "",
    target_checkin: "",
    target_prospect: "",
    target_closing: "",
  });
  const [salesTargetDraft, setSalesTargetDraft] = useState({
    id: null,
    dealer_id: dealerLocked ? user?.dealer_id || "all" : "all",
    salesman_id: "all",
    month: String(new Date().getMonth() + 1),
    year: String(new Date().getFullYear()),
    target_area: "",
    target_checkin: "",
    target_prospect: "",
    target_closing: "",
  });
  const [selectedSalesman, setSelectedSalesman] = useState(null);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [prospectStatusFilter, setProspectStatusFilter] = useState("all");

  const activeNav = useMemo(() => {
    return ADMIN_NAV.find((item) => item.path === location.pathname)?.key || "dashboard";
  }, [location.pathname]);

  const fetchDashboardData = useCallback(() => {
    api.get("/analytics/summary").then((r) => setKpi(r.data)).catch(() => {});
    api.get("/analytics/leaderboard").then((r) => setLeaderboard(r.data)).catch(() => {});
    api.get("/analytics/scorecard").then((r) => setScorecard(r.data)).catch(() => {});
    api.get("/dealers").then((r) => {
      setDealers([{ id: "all", name: "Semua Dealer", city: "" }, ...r.data.map((d) => ({
        id: d.id,
        code: d.code,
        name: d.name,
        city: d.city,
        group: d.group,
        region: normalizeRegion([d.city, d.name, d.group, d.code].filter(Boolean).join(" ")),
      }))]);
    }).catch(() => {});
    api.get("/coverage/checkins", { params: { days: 366 } }).then((r) => {
      setLiveMap(r.data);
    }).catch(() => {});
    api.get("/coverage/stats", { params: { days: 366 } }).then((r) => {
      setCoverageStats(r.data);
    }).catch(() => {});
    api.get("/coverage/settings").then((r) => {
      setCoverageSetting(r.data);
      setSettingDraft(r.data.coverage_valid_days);
    }).catch(() => {});
    api.get("/coverage/babel-target").then((r) => {
      setBabelTarget(r.data);
      setBabelTargetDraft({
        month: String(r.data.month || new Date().getMonth() + 1),
        year: String(r.data.year || new Date().getFullYear()),
        target_area: r.data.target_area ? String(r.data.target_area) : "",
        target_checkin: "",
        target_prospect: "",
        target_closing: "",
      });
    }).catch(() => {});
    api.get("/targets/settings").then((r) => {
      setTargetSettings(r.data);
      const now = new Date();
      const global = r.data.find((item) => item.scope_type === "global" && item.month === now.getMonth() + 1 && item.year === now.getFullYear());
      if (global) {
        setBabelTarget(global);
        setBabelTargetDraft({
          month: String(global.month),
          year: String(global.year),
          target_area: String(global.target_area),
          target_checkin: String(global.target_checkin),
          target_prospect: String(global.target_prospect),
          target_closing: String(global.target_closing),
        });
      }
      setDealerTargets(r.data.filter((item) => item.scope_type === "dealer"));
      setSalesTargets(r.data.filter((item) => item.scope_type === "sales"));
    }).catch(() => {});
    api.get("/analytics/coverage").then((r) => setHeatmap(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/prospects").then((r) => setProspects(r.data)).catch(() => {});
    api.get("/audit-logs").then((r) => setAuditLogs(r.data)).catch(() => setAuditLogs([]));
  }, []);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  useEffect(() => {
    if (dealerLocked && user?.dealer_id) {
      setDealer(user.dealer_id);
      setSalesman("all");
    }
  }, [dealerLocked, user?.dealer_id]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const coverage = params.get("coverage");
    const status = params.get("status");
    const date = params.get("date");
    const dealerParam = params.get("dealer");
    const salesmanParam = params.get("salesman");
    const cityParam = params.get("city");
    if (coverage) setCoverageFilter(coverage);
    if (status) setProspectStatusFilter(status);
    if (date === "today") setDateFilter(todayKey());
    else if (date !== null) setDateFilter(date);
    if (dealerParam && !dealerLocked) setDealer(dealerParam);
    if (salesmanParam) setSalesman(salesmanParam);
    if (cityParam) setCityFilter(cityParam);
  }, [dealerLocked, location.search]);

  const filterMatchesDate = useCallback((value) => {
    const key = dateKey(value);
    if (!key) return false;
    if (dateFilter) return key === dateFilter;
    if (monthFilter !== "all") return monthKey(value) === monthFilter;
    return true;
  }, [dateFilter, monthFilter]);

  const dealerLookup = useMemo(() => {
    const byId = new Map();
    const byName = new Map();
    dealers.forEach((item) => {
      if (item.id) byId.set(item.id, item);
      if (item.name) byName.set(item.name.toLowerCase(), item);
      if (item.code) byName.set(String(item.code).toLowerCase(), item);
    });
    return { byId, byName };
  }, [dealers]);

  const resolveDealerMeta = useCallback((dealerId, dealerName) => {
    return dealerLookup.byId.get(dealerId) || dealerLookup.byName.get(String(dealerName || "").toLowerCase()) || null;
  }, [dealerLookup]);

  const matchesCityFilter = useCallback((dealerId, dealerName) => {
    if (cityFilter === "all") return true;
    const meta = resolveDealerMeta(dealerId, dealerName);
    return (meta?.region || "") === cityFilter;
  }, [cityFilter, resolveDealerMeta]);

  const filteredUsers = useMemo(() => {
    return users.filter((item) => {
      const dealerMatch = dealer === "all" || item.dealer_id === dealer;
      const salesmanMatch = item.role !== "sales" || salesman === "all" || item.id === salesman;
      const cityMatch = matchesCityFilter(item.dealer_id, item.dealer);
      return dealerMatch && salesmanMatch && cityMatch;
    });
  }, [dealer, matchesCityFilter, salesman, users]);

  const filteredLiveMap = useMemo(() => {
    return liveMap.filter((item) => {
      const dealerMatch = dealer === "all" || item.dealer_id === dealer;
      const salesmanMatch = salesman === "all" || item.salesman_id === salesman;
      const dateMatch = filterMatchesDate(item.checkin_time);
      const cityMatch = matchesCityFilter(item.dealer_id, item.dealer_name || item.dealer_id);
      return dealerMatch && salesmanMatch && dateMatch && cityMatch;
    });
  }, [dealer, filterMatchesDate, liveMap, matchesCityFilter, salesman]);

  const filteredProspects = useMemo(() => {
    return prospects.filter((item) => {
      const dealerMatch = dealer === "all" || item.dealer_id === dealer;
      const salesmanMatch = salesman === "all" || item.salesman_id === salesman;
      const dateMatch = filterMatchesDate(item.created_at || item.visited_at || item.updated_at);
      const statusMatch = prospectStatusFilter === "all" || item.status === prospectStatusFilter;
      const cityMatch = matchesCityFilter(item.dealer_id, item.dealer_name || item.dealer_id);
      return dealerMatch && salesmanMatch && dateMatch && statusMatch && cityMatch;
    });
  }, [dealer, filterMatchesDate, matchesCityFilter, prospects, prospectStatusFilter, salesman]);

  const dealerOptions = useMemo(() => dealers
    .filter((item) => item.id === "all" || cityFilter === "all" || item.region === cityFilter)
    .map((item) => ({
      value: item.id,
      label: item.name,
    })), [cityFilter, dealers]);

  const availableSalesmen = useMemo(() => {
    return users
      .filter((item) => item.role === "sales")
      .filter((item) => dealer === "all" || item.dealer_id === dealer)
      .filter((item) => matchesCityFilter(item.dealer_id, item.dealer));
  }, [dealer, matchesCityFilter, users]);

  const salesmanOptions = useMemo(() => [
    { value: "all", label: "Semua Salesman" },
    ...availableSalesmen.map((item) => ({ value: item.id, label: item.name || item.email })),
  ], [availableSalesmen]);

  const targetSalesmanOptions = useMemo(() => [
    { value: "all", label: "Semua Salesman" },
    ...users
      .filter((item) => item.role === "sales")
      .filter((item) => salesTargetDraft.dealer_id === "all" || item.dealer_id === salesTargetDraft.dealer_id)
      .map((item) => ({ value: item.id, label: item.name || item.email })),
  ], [salesTargetDraft.dealer_id, users]);

  const canManageTargets = user?.role === "main_dealer";
  const manageableRoles = useMemo(() => {
    if (user?.role === "main_dealer") return ["main_dealer", "kepala_cabang", "team_leader", "sales"];
    if (user?.role === "kepala_cabang") return ["team_leader", "sales"];
    if (user?.role === "team_leader") return ["sales"];
    return [];
  }, [user?.role]);

  const branchHeadOptions = useMemo(() => users
    .filter((item) => item.role === "kepala_cabang")
    .filter((item) => !userDraft.dealer_id || item.dealer_id === userDraft.dealer_id)
    .map((item) => ({ value: item.id, label: item.name || item.email })), [userDraft.dealer_id, users]);

  const teamLeaderOptions = useMemo(() => users
    .filter((item) => item.role === "team_leader")
    .filter((item) => !userDraft.dealer_id || item.dealer_id === userDraft.dealer_id)
    .map((item) => ({ value: item.id, label: item.name || item.email })), [userDraft.dealer_id, users]);

  const managedUsers = useMemo(() => {
    const term = userSearch.trim().toLowerCase();
    return users.filter((item) => {
      const roleMatch = userRoleFilter === "all" || item.role === userRoleFilter;
      const dealerMatch = userDealerFilter === "all" || item.dealer_id === userDealerFilter;
      const statusMatch = userStatusFilter === "all" || (userStatusFilter === "active" ? item.active !== false : item.active === false);
      const searchMatch = !term || [item.name, item.email, item.phone].some((value) => String(value || "").toLowerCase().includes(term));
      return roleMatch && dealerMatch && statusMatch && searchMatch;
    });
  }, [userDealerFilter, userRoleFilter, userSearch, userStatusFilter, users]);

  const openCreateUser = () => {
    setUserDraft({ ...emptyUserDraft, role: manageableRoles.includes("sales") ? "sales" : manageableRoles[0] || "sales" });
    setUserModal({ mode: "create" });
  };

  const openEditUser = (row) => {
    setUserDraft({
      name: row.name || "",
      email: row.email || "",
      password: "",
      phone: row.phone || "",
      role: row.role || "sales",
      dealer_id: row.dealer_id || "",
      branch_head_id: row.branch_head_id || "",
      team_leader_id: row.team_leader_id || "",
      active: row.active !== false,
      id: row.id,
    });
    setUserModal({ mode: "edit" });
  };

  const reloadUsers = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };

  const resetFilters = () => {
    setDealer(dealerLocked && user?.dealer_id ? user.dealer_id : "all");
    setSalesman("all");
    setDateFilter("");
    setMonthFilter("all");
    setCityFilter("all");
    setCoverageFilter("all");
    setProspectStatusFilter("all");
  };

  const targetPeriod = useMemo(() => activeTargetPeriod(dateFilter, monthFilter), [dateFilter, monthFilter]);

  const relevantDealerTargets = useMemo(() => dealerTargets.filter((item) => {
    const monthMatch = item.month === targetPeriod.month;
    const yearMatch = item.year === targetPeriod.year;
    const dealerMatch = dealer === "all" || item.dealer_id === dealer;
    const cityMatch = matchesCityFilter(item.dealer_id, item.dealer_name || item.dealer_id);
    return monthMatch && yearMatch && dealerMatch && cityMatch;
  }), [dealer, dealerTargets, matchesCityFilter, targetPeriod.month, targetPeriod.year]);

  const relevantSalesTargets = useMemo(() => salesTargets.filter((item) => {
    const monthMatch = item.month === targetPeriod.month;
    const yearMatch = item.year === targetPeriod.year;
    const dealerMatch = dealer === "all" || item.dealer_id === dealer;
    const salesmanMatch = salesman === "all" || item.salesman_id === salesman;
    const cityMatch = matchesCityFilter(item.dealer_id, item.dealer_name || item.dealer_id);
    return monthMatch && yearMatch && dealerMatch && salesmanMatch && cityMatch;
  }), [dealer, matchesCityFilter, salesman, salesTargets, targetPeriod.month, targetPeriod.year]);

  const selectedDealerTarget = useMemo(() => {
    if (salesman !== "all") {
      const salesTarget = relevantSalesTargets.find((item) => item.salesman_id === salesman);
      if (salesTarget) return salesTarget;
    }
    if (dealer !== "all") {
      return relevantDealerTargets.find((item) => item.dealer_id === dealer) || null;
    }
    if (relevantDealerTargets.length === 1) return relevantDealerTargets[0];
    return null;
  }, [dealer, relevantDealerTargets, relevantSalesTargets, salesman]);

  const selectedSalesTarget = useMemo(() => {
    if (salesman === "all") return null;
    return relevantSalesTargets.find((item) => item.salesman_id === salesman) || null;
  }, [relevantSalesTargets, salesman]);

  const currentGlobalTarget = useMemo(() => {
    return targetSettings.find((item) => item.scope_type === "global" && item.month === targetPeriod.month && item.year === targetPeriod.year) || null;
  }, [targetPeriod.month, targetPeriod.year, targetSettings]);

  const coverageSummary = useMemo(() => {
    const counts = { valid: 0, expired: 0, prospect: 0, closing: 0, out_of_area: 0 };
    filteredLiveMap.forEach((item) => {
      const status = item.visual_status || item.status || "valid";
      if (counts[status] !== undefined) counts[status] += 1;
    });
    const targetArea = coverageStats?.target_area || 0;
    const areaValid = coverageStats?.area_valid ?? counts.valid + counts.prospect + counts.closing;
    return {
      ...counts,
      valid: areaValid,
      total: areaValid,
      targetArea,
      unvisited: coverageStats?.area_unvisited ?? Math.max(targetArea - areaValid, 0),
      score: Math.min(Number(coverageStats?.coverage_score ?? 0), 100),
      targetSet: coverageStats?.target_set ?? false,
    };
  }, [coverageStats, filteredLiveMap]);

  const ADMIN_KPIS = {
    totalSalesmen: filteredUsers.filter((item) => item.role === "sales").length,
    activeNow: filteredLiveMap.length,
    prospectsToday: filteredProspects.filter((item) => {
      const today = todayKey();
      return (item.created_at || "").startsWith(today) || (item.updated_at || "").startsWith(today);
    }).length,
    closingMonth: filteredProspects.filter((item) => item.status === "closing").length,
    coverageOverall: coverageSummary.score,
    areaValid: coverageSummary.valid,
    areaExpired: coverageStats?.area_expired ?? coverageSummary.expired,
    areaUnvisited: coverageSummary.unvisited,
    targetArea: coverageSummary.targetArea,
    targetSet: coverageSummary.targetSet,
    coverageValidityDays: coverageStats?.coverage_valid_days ?? coverageSetting.coverage_valid_days,
    actualCheckin: coverageStats?.actual_checkin ?? coverageStats?.total ?? 0,
    targetCheckin: coverageStats?.target_checkin ?? currentGlobalTarget?.target_checkin ?? 0,
    actualProspect: coverageStats?.actual_prospect ?? filteredProspects.length,
    targetProspect: coverageStats?.target_prospect ?? currentGlobalTarget?.target_prospect ?? 0,
    actualClosing: coverageStats?.actual_closing ?? filteredProspects.filter((item) => item.status === "closing").length,
    targetClosing: coverageStats?.target_closing ?? currentGlobalTarget?.target_closing ?? 0,
    totalDealers: dealer === "all"
      ? dealers.filter((item) => item.id !== "all" && (cityFilter === "all" || item.region === cityFilter)).length
      : 1,
  };

  const WEEKLY_TREND = useMemo(() => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const dateStr = dateKey(d);
      days.push({ dayStr: dateStr, day: d.toLocaleDateString("id-ID", { weekday: "short" }), prospek: 0, closing: 0 });
    }
    prospects
      .filter((p) => (dealer === "all" || p.dealer_id === dealer) && (salesman === "all" || p.salesman_id === salesman))
      .filter((p) => matchesCityFilter(p.dealer_id, p.dealer_name || p.dealer_id))
      .forEach(p => {
      const d = (p.created_at || p.updated_at || "").slice(0, 10);
      const match = days.find(x => x.dayStr === d);
      if (match) {
        match.prospek += 1;
        if (p.status === "closing") match.closing += 1;
      }
    });
    return days;
  }, [dealer, matchesCityFilter, prospects, salesman]);
  const PRODUCT_DISTRIBUTION = useMemo(() => {
    const counts = prospects
      .filter((item) => (dealer === "all" || item.dealer_id === dealer) && (salesman === "all" || item.salesman_id === salesman))
      .filter((item) => matchesCityFilter(item.dealer_id, item.dealer_name || item.dealer_id))
      .filter((item) => {
        const key = dateKey(item.created_at || item.updated_at);
        return key && Number(key.slice(5, 7)) === targetPeriod.month && Number(key.slice(0, 4)) === targetPeriod.year;
      })
      .reduce((acc, item) => {
      const key = productInterest(item) || "Lainnya";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [dealer, matchesCityFilter, prospects, salesman, targetPeriod.month, targetPeriod.year]);
  const TOP_SALESMEN = useMemo(() => {
    const checkinBySales = filteredLiveMap.reduce((acc, item) => {
      acc[item.salesman_id] = (acc[item.salesman_id] || 0) + 1;
      return acc;
    }, {});
    const rows = filteredProspects.reduce((acc, item) => {
      const id = item.salesman_id || "unknown";
      acc[id] = acc[id] || {
        id,
        name: item.salesman_name || "—",
        dealer: resolveDealerMeta(item.dealer_id, item.dealer_name || item.dealer_id)?.name || "Dealer tidak ditemukan",
        closing: 0,
        prospects: 0,
        visits: checkinBySales[id] || 0
      };
      acc[id].prospects += 1;
      if (item.status === "closing") acc[id].closing += 1;
      return acc;
    }, {});
    filteredLiveMap.forEach(item => {
      const id = item.salesman_id;
      if (!rows[id]) {
        rows[id] = {
          id,
          name: item.salesman_name || "—",
          dealer: resolveDealerMeta(item.dealer_id, item.dealer_id)?.name || "Dealer tidak ditemukan",
          closing: 0,
          prospects: 0,
          visits: checkinBySales[id] || 0,
        };
      }
    });
    return Object.values(rows).map(s => {
      const target = relevantSalesTargets.find(t => t.salesman_id === s.id);
      const coveragePct = target?.target_checkin ? Math.min((s.visits / target.target_checkin) * 100, 100) : (s.visits > 0 ? 100 : 0);
      const prospectPct = target?.target_prospect ? Math.min((s.prospects / target.target_prospect) * 100, 100) : (s.prospects > 0 ? 100 : 0);
      const closingPct = target?.target_closing ? Math.min((s.closing / target.target_closing) * 100, 100) : (s.closing > 0 ? 100 : 0);
      const score = Math.round((coveragePct * 0.4) + (prospectPct * 0.3) + (closingPct * 0.3));
      return { ...s, score };
    }).sort((a, b) => b.score - a.score || b.closing - a.closing).slice(0, 10);
  }, [filteredProspects, filteredLiveMap, relevantSalesTargets, resolveDealerMeta]);
  const LIVE_SALESMEN = useMemo(() => {
    const visitsBySalesAndDate = {};
    filteredLiveMap.forEach(p => {
      const date = p.checkin_time?.slice(0, 10);
      if (!date) return;
      visitsBySalesAndDate[p.salesman_id] = visitsBySalesAndDate[p.salesman_id] || {};
      visitsBySalesAndDate[p.salesman_id][date] = (visitsBySalesAndDate[p.salesman_id][date] || 0) + 1;
    });
    const prospekBySalesAndDate = {};
    const closingBySalesAndDate = {};
    filteredProspects.forEach(p => {
      const date = (p.created_at || p.updated_at || "").slice(0, 10);
      if (!date) return;
      prospekBySalesAndDate[p.salesman_id] = prospekBySalesAndDate[p.salesman_id] || {};
      prospekBySalesAndDate[p.salesman_id][date] = (prospekBySalesAndDate[p.salesman_id][date] || 0) + 1;
      if (p.status === "closing") {
        closingBySalesAndDate[p.salesman_id] = closingBySalesAndDate[p.salesman_id] || {};
        closingBySalesAndDate[p.salesman_id][date] = (closingBySalesAndDate[p.salesman_id][date] || 0) + 1;
      }
    });
    
    return filteredLiveMap.map((p) => {
      const date = p.checkin_time?.slice(0, 10);
      const isToday = date === todayKey();
      const today = todayKey();
      return {
        id: p.id || `${p.salesman_id}-${p.checkin_time}`,
        salesmanId: p.salesman_id,
        name: p.salesman_name || "—",
        dealer: resolveDealerMeta(p.dealer_id, p.dealer_id)?.name || "Dealer tidak ditemukan",
        coord: { lat: p.latitude, lng: p.longitude },
        status: p.visual_status || p.status || "valid",
        visitsToday: isToday ? (visitsBySalesAndDate[p.salesman_id][date] || 0) : 0,
        prospectsToday: prospekBySalesAndDate[p.salesman_id]?.[today] || 0,
        closingToday: closingBySalesAndDate[p.salesman_id]?.[today] || 0,
        lastUpdate: new Date(p.checkin_time).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }),
        checkinDate: date,
        checkinTime: p.checkin_time,
        accuracy: p.accuracy,
        photoUrl: p.photo_url,
        validUntil: p.valid_until,
      };
    });
  }, [filteredLiveMap, filteredProspects, resolveDealerMeta]);
  const COVERAGE_HOTSPOTS = useMemo(() => filteredLiveMap.filter((p) => p.latitude && p.longitude && p.visual_status !== "out_of_area").map((p) => ({
    lat: p.latitude,
    lng: p.longitude,
    intensity: 0.5,
  })), [filteredLiveMap]);
  const SCORECARD = useMemo(() => {
    const checkinByDealer = filteredLiveMap.reduce((acc, item) => {
      acc[item.dealer_id] = (acc[item.dealer_id] || 0) + 1;
      return acc;
    }, {});
    const rows = filteredProspects.reduce((acc, item) => {
      const id = item.dealer_id || "—";
      acc[id] = acc[id] || { dealer_id: id, dealer: resolveDealerMeta(id, id)?.name || "Dealer tidak ditemukan", closing: 0, prospects: 0, hot: 0, visits: checkinByDealer[id] || 0 };
      acc[id].prospects += 1;
      if (item.status === "closing") acc[id].closing += 1;
      if (item.status === "hot") acc[id].hot += 1;
      return acc;
    }, {});
    relevantDealerTargets.forEach(t => {
      if (!rows[t.dealer_id]) {
        rows[t.dealer_id] = { dealer_id: t.dealer_id, dealer: resolveDealerMeta(t.dealer_id, t.dealer_name)?.name || "Dealer tidak ditemukan", closing: 0, prospects: 0, hot: 0, visits: checkinByDealer[t.dealer_id] || 0 };
      }
    });
    return Object.values(rows).map(c => {
      const target = relevantDealerTargets.find(t => t.dealer_id === c.dealer_id);
      const coveragePct = target?.target_checkin ? Math.min((c.visits / target.target_checkin) * 100, 100) : (c.visits > 0 ? 100 : 0);
      const prospectPct = target?.target_prospect ? Math.min((c.prospects / target.target_prospect) * 100, 100) : (c.prospects > 0 ? 100 : 0);
      const closingPct = target?.target_closing ? Math.min((c.closing / target.target_closing) * 100, 100) : (c.closing > 0 ? 100 : 0);
      const achievement = Math.round((coveragePct + prospectPct + closingPct) / 3);
      return { ...c, coveragePct: Math.round(coveragePct), achievement };
    }).sort((a, b) => b.achievement - a.achievement || b.closing - a.closing);
  }, [filteredProspects, filteredLiveMap, relevantDealerTargets, resolveDealerMeta]);
  const ADMIN_ACTIVITY = useMemo(() => {
    const checkinEvents = filteredLiveMap.map(row => ({
      id: `checkin-${row.id}`,
      type: row.photo_url ? "evidence" : "status",
      salesman: row.salesman_name || "Sales",
      dealer: row.dealer_id || "Dealer",
      action: row.photo_url ? "Upload Foto Evidence" : "Check-In Area",
      detail: STATUS_META[row.visual_status || row.status]?.label || row.status,
      timeValue: new Date(row.checkin_time).getTime(),
      time: new Date(row.checkin_time).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }),
    }));
    const prospectEvents = filteredProspects.map(row => {
      const isClosing = row.status === "closing";
      return {
        id: `prospect-${row.id}`,
        type: isClosing ? "closing" : "prospect",
        salesman: row.salesman_name || "Sales",
        dealer: row.dealer_id || "Dealer",
        action: isClosing ? "Status Prospek Menjadi Closing" : "Tambah Prospek Baru",
        detail: row.name || "Prospek",
        timeValue: new Date(row.updated_at || row.created_at).getTime(),
        time: new Date(row.updated_at || row.created_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }),
      }
    });
    return [...checkinEvents, ...prospectEvents].sort((a, b) => b.timeValue - a.timeValue).slice(0, 20);
  }, [filteredLiveMap, filteredProspects]);
  const DEALERS = dealers;

  const salesmanRows = useMemo(() => {
    const salesUsers = filteredUsers.filter((u) => u.role === "sales");
    const checkinBySales = filteredLiveMap.reduce((acc, item) => {
      acc[item.salesman_id] = acc[item.salesman_id] || { visits: 0, last: null };
      acc[item.salesman_id].visits += 1;
      if (!acc[item.salesman_id].last || new Date(item.checkin_time) > new Date(acc[item.salesman_id].last)) {
        acc[item.salesman_id].last = item.checkin_time;
      }
      return acc;
    }, {});
    const prospectsBySales = filteredProspects.reduce((acc, item) => {
      acc[item.salesman_id] = (acc[item.salesman_id] || 0) + 1;
      return acc;
    }, {});
    return salesUsers.map((u) => {
      const salesTarget = relevantSalesTargets.find((item) => item.salesman_id === u.id);
      const visits = checkinBySales[u.id]?.visits || 0;
      const prospects = prospectsBySales[u.id] || 0;
      const closing = filteredProspects.filter((item) => item.salesman_id === u.id && item.status === "closing").length;
      return {
        id: u.id,
        name: u.name || "—",
        dealer: resolveDealerMeta(u.dealer_id, u.dealer)?.name || u.dealer || u.dealer_id || "—",
        city: resolveDealerMeta(u.dealer_id, u.dealer)?.region || "",
        active: u.active !== false,
        visits,
        prospects,
        closing,
        evidence: filteredLiveMap.filter((item) => item.salesman_id === u.id && item.photo_url).length,
        targetVisits: salesTarget?.target_checkin || 0,
        targetProspects: salesTarget?.target_prospect || 0,
        targetClosing: salesTarget?.target_closing || 0,
        gapVisits: Math.max((salesTarget?.target_checkin || 0) - visits, 0),
        gapProspects: Math.max((salesTarget?.target_prospect || 0) - prospects, 0),
        gapClosing: Math.max((salesTarget?.target_closing || 0) - closing, 0),
        lastCheckin: formatDateTime(checkinBySales[u.id]?.last),
      };
    });
  }, [filteredLiveMap, filteredProspects, filteredUsers, relevantSalesTargets, resolveDealerMeta]);

  const evidenceRows = useMemo(() => {
    return filteredLiveMap.filter((row) => row.photo_url).map((row) => ({
      ...row,
      photo_url: row.photo_url?.startsWith("http") ? row.photo_url : row.photo_url,
    }));
  }, [filteredLiveMap]);

  const dealerRows = useMemo(() => {
    return dealers
      .filter((d) => d.id !== "all")
      .filter((d) => (dealer === "all" || d.id === dealer) && (cityFilter === "all" || d.region === cityFilter))
      .map((d) => {
      const dealerSales = salesmanRows.filter((s) => s.dealer === d.id || s.dealer === d.name);
      const dealerProspects = filteredProspects.filter((p) => p.dealer_id === d.id || p.dealer_id === d.name);
      const dealerVisits = filteredLiveMap.filter((v) => v.dealer_id === d.id || v.dealer_id === d.name);
      const dealerTarget = relevantDealerTargets.find((item) => item.dealer_id === d.id);
      const closing = dealerProspects.filter((item) => item.status === "closing").length;
      return {
        id: d.id,
        name: d.name,
        sales: dealerSales.length,
        visits: dealerVisits.length,
        prospects: dealerProspects.length,
        closing,
        targetVisits: dealerTarget?.target_checkin || 0,
        targetProspects: dealerTarget?.target_prospect || 0,
        targetClosing: dealerTarget?.target_closing || 0,
        coverageAchievement: [
          achievementLabel(dealerVisits.length, dealerTarget?.target_checkin || 0),
          achievementLabel(dealerProspects.length, dealerTarget?.target_prospect || 0),
          achievementLabel(closing, dealerTarget?.target_closing || 0),
        ].join(" | "),
      };
    });
  }, [cityFilter, dealers, dealer, filteredLiveMap, filteredProspects, relevantDealerTargets, salesmanRows]);

  const auditRows = useMemo(() => {
    if (auditLogs.length > 0) {
      return auditLogs.map((row) => ({
        id: row.id,
        action: row.action || "Audit",
        user: row.user_name || row.user_id || "System",
        detail: [row.entity_type, row.metadata?.status, row.dealer_id].filter(Boolean).join(" · "),
        time: formatDateTime(row.created_at),
      }));
    }
    const checkins = filteredLiveMap.slice(0, 8).map((row) => ({
      id: `checkin-${row.id}`,
      action: "Check-in",
      user: row.salesman_name || "Sales",
      detail: `${row.dealer_id || "Dealer"} · ${STATUS_META[row.visual_status || row.status]?.label || row.status}`,
      time: formatDateTime(row.checkin_time),
    }));
    const prospectEvents = filteredProspects.slice(0, 8).map((row) => ({
      id: `prospect-${row.id}`,
      action: "Update prospek",
      user: row.salesman_name || "Sales",
      detail: row.name || "Prospek",
      time: formatDateTime(row.updated_at || row.created_at),
    }));
    return [...checkins, ...prospectEvents].sort((a, b) => (a.time < b.time ? 1 : -1)).slice(0, 20);
  }, [auditLogs, filteredLiveMap, filteredProspects]);

  const filteredSalesmen = useMemo(() => {
    return LIVE_SALESMEN.filter((s) => {
      const dealerMatch =
        dealer === "all" || (s.dealer || "").toLowerCase().includes(dealer.toLowerCase());
      const searchMatch = !search || (s.name || "").toLowerCase().includes(search.toLowerCase());
      const statusMatch = coverageFilter === "all" || s.status === coverageFilter;
      const dateMatch = !dateFilter || s.checkinDate === dateFilter;
      return dealerMatch && searchMatch && statusMatch && dateMatch;
    });
  }, [LIVE_SALESMEN, coverageFilter, dateFilter, dealer, search]);

  const saveCoverageSetting = async () => {
    const days = Number(settingDraft);
    if (!Number.isInteger(days) || days < 1 || days > 365) {
      toast.error("Coverage validity wajib integer 1-365 hari.");
      return;
    }
    const { data } = await api.put("/coverage/settings", { coverage_valid_days: days });
    setCoverageSetting(data);
    const [statsRes, checkinsRes] = await Promise.all([
      api.get("/coverage/stats", { params: { days: 366 } }),
      api.get("/coverage/checkins", { params: { days: 366 } }),
    ]);
    setCoverageStats(statsRes.data);
    setLiveMap(checkinsRes.data);
    toast.success("Coverage validity berhasil disimpan.");
  };

  const saveBabelTarget = async () => {
    const targetArea = Number(babelTargetDraft.target_area);
    const targetCheckin = Number(babelTargetDraft.target_checkin || 0);
    const targetProspect = Number(babelTargetDraft.target_prospect || 0);
    const targetClosing = Number(babelTargetDraft.target_closing || 0);
    if (!Number.isInteger(targetArea) || targetArea < 1 || targetArea > 7) {
      toast.error("Target Kabupaten/Kota wajib integer 1-7.");
      return;
    }
    if (![targetCheckin, targetProspect, targetClosing].every((value) => Number.isInteger(value) && value >= 0)) {
      toast.error("Target check-in, prospek, dan closing wajib integer minimal 0.");
      return;
    }
    const { data } = await api.post("/targets/settings", {
      scope_type: "global",
      scope_id: null,
      month: Number(babelTargetDraft.month),
      year: Number(babelTargetDraft.year),
      target_area: targetArea,
      target_checkin: targetCheckin,
      target_prospect: targetProspect,
      target_closing: targetClosing,
    });
    setBabelTarget(data);
    const [statsRes, targetRes] = await Promise.all([
      api.get("/coverage/stats", { params: { month: Number(babelTargetDraft.month), year: Number(babelTargetDraft.year) } }),
      api.get("/targets/settings"),
    ]);
    setCoverageStats(statsRes.data);
    setTargetSettings(targetRes.data);
    toast.success("Target Babel Coverage berhasil disimpan.");
  };

  const reloadTargets = async () => {
    const [dealerRes, salesRes, settingsRes] = await Promise.all([
      api.get("/targets/dealers"),
      api.get("/targets/sales"),
      api.get("/targets/settings"),
    ]);
    setDealerTargets(dealerRes.data);
    setSalesTargets(salesRes.data);
    setTargetSettings(settingsRes.data);
  };

  const saveManagedUser = async () => {
    const payload = {
      name: userDraft.name.trim(),
      email: userDraft.email.trim(),
      phone: userDraft.phone.trim() || null,
      role: userDraft.role,
      dealer_id: userDraft.role === "main_dealer" ? null : userDraft.dealer_id || null,
      branch_head_id: userDraft.role === "team_leader" ? userDraft.branch_head_id || null : userDraft.role === "sales" ? userDraft.branch_head_id || null : null,
      team_leader_id: userDraft.role === "sales" ? userDraft.team_leader_id || null : null,
      active: userDraft.active,
    };
    if (!payload.name || !payload.email) {
      toast.error("Nama dan email wajib diisi.");
      return;
    }
    if (userDraft.role !== "main_dealer" && !payload.dealer_id) {
      toast.error("Dealer wajib dipilih.");
      return;
    }
    if (userDraft.role === "team_leader" && !payload.branch_head_id) {
      toast.error("Kepala Cabang wajib dipilih.");
      return;
    }
    if (userDraft.role === "sales" && !payload.team_leader_id) {
      toast.error("Team Leader wajib dipilih.");
      return;
    }
    if (userModal?.mode === "create") {
      if (!userDraft.password || userDraft.password.length < 6) {
        toast.error("Password minimal 6 karakter.");
        return;
      }
      await api.post("/users", { ...payload, password: userDraft.password });
      toast.success("User berhasil dibuat.");
    } else {
      await api.patch(`/users/${userDraft.id}`, payload);
      toast.success("User berhasil diperbarui.");
    }
    setUserModal(null);
    await reloadUsers();
    fetchDashboardData();
  };

  const resetManagedPassword = async () => {
    if (!resetTarget || resetPasswordDraft.length < 6) {
      toast.error("Password baru minimal 6 karakter.");
      return;
    }
    await api.post(`/users/${resetTarget.id}/reset-password`, { password: resetPasswordDraft });
    toast.success("Password berhasil direset.");
    setResetTarget(null);
    setResetPasswordDraft("");
    await reloadUsers();
  };

  const toggleManagedUser = async (row) => {
    await api.post(`/users/${row.id}/status`, null, { params: { active: row.active === false } });
    toast.success(row.active === false ? "User diaktifkan." : "User dinonaktifkan.");
    await reloadUsers();
    fetchDashboardData();
  };

  const deleteUser = async () => {
    if (!deleteUserTarget) return;
    try {
      const { data } = await api.delete(`/users/${deleteUserTarget.id}`);
      if (data.soft_deleted) {
        toast.success("User di-nonaktifkan (soft delete) karena memiliki data aktivitas.");
      } else {
        toast.success("User berhasil dihapus secara permanen.");
      }
      setDeleteUserTarget(null);
      await reloadUsers();
      fetchDashboardData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menghapus user.");
    }
  };

  const updateAdminProspectStatus = async (prospect, status) => {
    const { data } = await api.patch(`/prospects/${prospect.id}`, { status });
    setProspects((rows) => rows.map((row) => (row.id === data.id ? data : row)));
    setSelectedProspect(data);
    fetchDashboardData();
    toast.success("Status prospek diperbarui.");
  };

  const saveDealerTarget = async () => {
    await api.post("/targets/dealers", {
      dealer_id: dealerTargetDraft.dealer_id,
      month: Number(dealerTargetDraft.month),
      year: Number(dealerTargetDraft.year),
      target_checkin: Number(dealerTargetDraft.target_checkin || 0),
      target_prospect: Number(dealerTargetDraft.target_prospect || 0),
      target_closing: Number(dealerTargetDraft.target_closing || 0),
    });
    await api.post("/targets/settings", {
      scope_type: "dealer",
      scope_id: dealerTargetDraft.dealer_id,
      month: Number(dealerTargetDraft.month),
      year: Number(dealerTargetDraft.year),
      target_area: 1,
      target_checkin: Number(dealerTargetDraft.target_checkin || 0),
      target_prospect: Number(dealerTargetDraft.target_prospect || 0),
      target_closing: Number(dealerTargetDraft.target_closing || 0),
    });
    await reloadTargets();
  };

  const saveSalesTarget = async () => {
    await api.post("/targets/sales", {
      dealer_id: salesTargetDraft.dealer_id,
      salesman_id: salesTargetDraft.salesman_id,
      month: Number(salesTargetDraft.month),
      year: Number(salesTargetDraft.year),
      target_checkin: Number(salesTargetDraft.target_checkin || 0),
      target_prospect: Number(salesTargetDraft.target_prospect || 0),
      target_closing: Number(salesTargetDraft.target_closing || 0),
    });
    await api.post("/targets/settings", {
      scope_type: "sales",
      scope_id: salesTargetDraft.salesman_id,
      month: Number(salesTargetDraft.month),
      year: Number(salesTargetDraft.year),
      target_area: 1,
      target_checkin: Number(salesTargetDraft.target_checkin || 0),
      target_prospect: Number(salesTargetDraft.target_prospect || 0),
      target_closing: Number(salesTargetDraft.target_closing || 0),
    });
    await reloadTargets();
  };

  const deleteDealerTarget = async (id) => {
    await api.delete(`/targets/dealers/${id}`);
    await reloadTargets();
  };

  const deleteSalesTarget = async (id) => {
    await api.delete(`/targets/sales/${id}`);
    await reloadTargets();
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const navigateWithFilter = (path, params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") query.set(key, value);
    });
    navigate(`${path}${query.toString() ? `?${query.toString()}` : ""}`);
  };

  const handleGlobalSearch = () => {
    const term = search.trim().toLowerCase();
    if (!term) return;
    const matchedSalesman = users.find((item) =>
      item.role === "sales" && [item.name, item.email, item.dealer].some((value) => (value || "").toLowerCase().includes(term))
    );
    if (matchedSalesman) {
      setSalesman(matchedSalesman.id);
      navigateWithFilter("/admin/salesman", { salesman: matchedSalesman.id });
      return;
    }
    const matchedProspect = prospects.find((item) =>
      [item.name, item.phone, item.salesman_name, item.interested_product].some((value) => (value || "").toLowerCase().includes(term))
    );
    if (matchedProspect) {
      setSelectedProspect(matchedProspect);
      navigate("/admin/prospek");
      return;
    }
    const matchedDealer = dealers.find((item) => item.id !== "all" && (item.name || "").toLowerCase().includes(term));
    if (matchedDealer) {
      setDealer(matchedDealer.id);
      setSalesman("all");
      setCityFilter(matchedDealer.region || "all");
      navigateWithFilter("/admin/dealer", { dealer: matchedDealer.id, city: matchedDealer.region || undefined });
    }
  };

  const handleDownloadExcel = async () => {
    try {
      const rows = [
        ["Section", "Name", "Dealer", "Salesman", "Status", "Date", "Latitude", "Longitude", "Accuracy"],
        ...salesmanRows.map((row) => ["Salesman", row.name, row.dealer, row.name, row.active ? "active" : "inactive", row.lastCheckin, "", "", ""]),
        ...filteredProspects.map((row) => ["Prospek", row.name, row.dealer_id, row.salesman_name, row.status, row.created_at, row.coord?.lat, row.coord?.lng, ""]),
        ...filteredLiveMap.map((row) => ["Coverage", row.id, row.dealer_id, row.salesman_name, row.visual_status || row.status, row.checkin_time, row.latitude, row.longitude, row.accuracy]),
      ];
      const csv = rows.map((row) => row.map((cell) => `"${String(cell ?? "").replaceAll('"', '""')}"`).join(",")).join("\n");
      const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `hscis_filtered_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen w-full bg-honda-bg flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-honda-sidebar text-white sticky top-0 h-screen px-4 py-6">
        <div className="flex items-center gap-2 px-2 mb-8">
          <div className="w-9 h-9 rounded-lg bg-honda-red flex items-center justify-center font-heading font-extrabold">
            H
          </div>
          <div>
            <p className="font-heading font-extrabold text-base leading-none">SALES TRACK</p>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 mt-1">
              Command Center
            </p>
          </div>
        </div>

        <p className="text-[10px] uppercase tracking-wider text-gray-500 px-3 mb-2">
          Operasional
        </p>
        <nav className="space-y-1">
          {ADMIN_NAV.filter((item) => item.section === "Operasional").map((item) => (
            <SidebarLink
              key={item.key}
              icon={item.icon}
              label={item.label}
              badge={item.badge === "live" ? LIVE_SALESMEN.length : item.badge}
              active={activeNav === item.key}
              testId={`nav-${item.key}`}
              onClick={() => navigate(item.path)}
            />
          ))}
        </nav>

        <p className="text-[10px] uppercase tracking-wider text-gray-500 px-3 mt-6 mb-2">
          Intelligence
        </p>
        <nav className="space-y-1">
          {ADMIN_NAV.filter((item) => item.section === "Intelligence").map((item) => (
            <SidebarLink
              key={item.key}
              icon={item.icon}
              label={item.label}
              active={activeNav === item.key}
              testId={`nav-${item.key}`}
              onClick={() => navigate(item.path)}
            />
          ))}
        </nav>

        <div className="mt-auto pt-6">
          <div className="rounded-2xl bg-gradient-to-br from-honda-red to-[#9F140E] p-4">
            <p className="font-heading font-extrabold text-base leading-tight">
              Babel Coverage
            </p>
            <p className="text-xs text-white/85 mt-1">
              {ADMIN_KPIS.targetSet ? `${ADMIN_KPIS.areaValid} dari ${ADMIN_KPIS.targetArea} kabupaten/kota bulan ini.` : "Target Babel belum diset"}
            </p>
            <div className="mt-3 h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-white rounded-full"
                style={{ width: `${Math.min(ADMIN_KPIS.coverageOverall, 100)}%` }}
              />
            </div>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="w-full mt-4 flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-gray-300 hover:bg-honda-sidebar-hover hover:text-white"
          >
            <LogOut size={16} /> Keluar
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-honda-border px-6 py-3.5 flex items-center gap-4">
          <div className="lg:hidden flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-honda-red flex items-center justify-center text-white font-heading font-extrabold text-sm">
              H
            </div>
            <p className="font-heading font-extrabold text-sm">SALES TRACK</p>
          </div>
          <div className="hidden lg:block">
            <p className="font-heading font-extrabold text-lg text-honda-ink leading-none">
              Sales Coverage Intelligence
            </p>
            <p className="text-[11px] text-honda-muted mt-1">
              Live monitoring · Honda Bangka Belitung Region
            </p>
          </div>

          <div className="flex-1 max-w-md relative ml-auto">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-honda-subtle"
              size={15}
            />
            <input
              data-testid="admin-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") handleGlobalSearch();
              }}
              placeholder="Cari salesman, prospek, dealer..."
              className="w-full bg-honda-bg border border-honda-border rounded-full pl-9 pr-4 py-2 text-sm placeholder:text-honda-subtle focus:outline-none focus:ring-2 focus:ring-honda-red/30 focus:border-honda-red"
            />
          </div>

          <ModeToggle />

          <button
            data-testid="admin-notification-btn"
            onClick={() => navigate("/admin/audit-log")}
            className="w-9 h-9 rounded-full bg-honda-bg hover:bg-honda-red-light flex items-center justify-center relative transition-colors"
          >
            <Bell size={16} />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-honda-red rounded-full" />
          </button>

          <button
            onClick={handleDownloadExcel}
            className="hidden md:flex items-center gap-2 bg-honda-red text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-[#9F140E] transition-colors"
          >
            <Download size={16} /> Export Excel
          </button>

          <div className="flex items-center gap-2.5 pl-3 border-l border-honda-border">
            <SafeImage
              src={user?.avatar}
              alt={user?.name || "User"}
              fallbackName={user?.name}
              className="h-9 w-9 rounded-full border-2 border-honda-border object-cover"
            />
            <div className="hidden md:block">
              <p className="text-sm font-semibold text-honda-ink leading-none">
                {user?.name}
              </p>
              <p className="text-[11px] text-honda-muted mt-1">
                {ROLE_LABELS[user?.role]}
              </p>
            </div>
            <button
              onClick={logout}
              title="Logout"
              className="ml-2 w-9 h-9 rounded-full bg-red-50 text-honda-red hover:bg-honda-red hover:text-white flex items-center justify-center transition-colors"
            >
              <LogOut size={16} />
            </button>
          </div>
        </header>

        <AdminFilterBar
          showDealerFilter={user?.role !== "sales"}
          dealer={dealer}
          setDealer={setDealer}
          dealerOptions={dealerOptions}
          showSalesmanFilter={user?.role !== "sales"}
          salesman={salesman}
          setSalesman={setSalesman}
          salesmanOptions={salesmanOptions}
          dateFilter={dateFilter}
          setDateFilter={setDateFilter}
          monthFilter={monthFilter}
          setMonthFilter={setMonthFilter}
          cityFilter={cityFilter}
          setCityFilter={setCityFilter}
          resetFilters={resetFilters}
          dealerLocked={dealerLocked}
        />

        <div className="lg:hidden px-6 pt-4 overflow-x-auto">
          <div className="flex min-w-max gap-2">
            {ADMIN_NAV.map((item) => (
              <button
                key={item.key}
                onClick={() => navigate(item.path)}
                className={`rounded-full px-3 py-2 text-xs font-bold ${
                  activeNav === item.key
                    ? "bg-honda-red text-white"
                    : "bg-white text-honda-muted border border-honda-border"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* KPI Cards */}
        <section className="px-6 pt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            icon={Compass}
            label="Coverage Babel"
            value={`${ADMIN_KPIS.areaValid}/${ADMIN_KPIS.targetArea || 0}`}
            sub="Kabupaten/Kota"
            accent="dark"
            trend={4.2}
            testId="kpi-coverage-admin"
            onClick={() => navigate("/admin/analytics")}
          />
          <StatCard
            icon={Target}
            label="Check-In Bulan Ini"
            value={`${ADMIN_KPIS.actualCheckin}/${ADMIN_KPIS.targetCheckin || 0}`}
            sub={`${coverageStats?.checkin_score ?? 0}% target`}
            accent="red"
            trend={12.8}
            testId="kpi-checkin-month"
            onClick={() => navigate("/admin/coverage-map")}
          />
          <StatCard
            icon={Users}
            label="Prospek Bulan Ini"
            value={`${ADMIN_KPIS.actualProspect}/${ADMIN_KPIS.targetProspect || 0}`}
            sub={`${coverageStats?.prospect_score ?? 0}% target`}
            accent="light"
            trend={7.6}
            testId="kpi-prospects-month"
            onClick={() => navigateWithFilter("/admin/prospek", { status: "all" })}
          />
          <StatCard
            icon={Award}
            label="Closing Bulan Ini"
            value={`${ADMIN_KPIS.actualClosing}/${ADMIN_KPIS.targetClosing || 0}`}
            sub={`${coverageStats?.closing_score ?? 0}% target`}
            accent="blue"
            trend={3.1}
            testId="kpi-closing-month-admin"
            onClick={() => navigateWithFilter("/admin/prospek", { status: "closing" })}
          />
        </section>

        <section className="px-6 pt-3 grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard
            icon={CheckCircle2}
            label="Area Valid"
            value={ADMIN_KPIS.areaValid}
            sub={`Validity ${ADMIN_KPIS.coverageValidityDays} hari`}
            accent="blue"
            testId="kpi-area-valid"
            onClick={() => navigateWithFilter("/admin/coverage-map", { coverage: "valid" })}
          />
          <StatCard
            icon={AlertTriangle}
            label="Area Expired"
            value={ADMIN_KPIS.areaExpired}
            sub={`${ADMIN_KPIS.areaExpired} perlu revisit`}
            accent="red"
            testId="kpi-area-expired"
            onClick={() => navigateWithFilter("/admin/coverage-map", { coverage: "expired" })}
          />
          <StatCard
            icon={MapPin}
            label="Belum Dikunjungi"
            value={ADMIN_KPIS.areaUnvisited}
            sub="Target area tersisa"
            accent="dark"
            testId="kpi-area-unvisited"
            onClick={() => navigateWithFilter("/admin/coverage-map", { coverage: "all" })}
          />
          <StatCard
            icon={Compass}
            label="Perlu Revisit"
            value={ADMIN_KPIS.areaExpired}
            sub="Prioritas minggu ini"
            accent="light"
            testId="kpi-area-revisit"
            onClick={() => navigateWithFilter("/admin/coverage-map", { coverage: "expired" })}
          />
          <div className="honda-card p-4">
            <p className="text-[10px] uppercase tracking-wider text-honda-muted font-bold">
              Coverage Validity
            </p>
            <div className="mt-2 flex items-center gap-2">
              <select
                value={settingDraft}
                onChange={(e) => setSettingDraft(e.target.value)}
                disabled={user?.role !== "main_dealer"}
                className="min-w-0 flex-1 rounded-lg border border-honda-border px-2 py-2 text-sm bg-white text-honda-ink"
              >
                {[7, 14, 30, 45, 60].map((days) => (
                  <option key={days} value={days}>{days} hari</option>
                ))}
                <option value={settingDraft}>Custom</option>
              </select>
              {user?.role === "main_dealer" && (
                <button
                  onClick={() => saveCoverageSetting().catch(() => {})}
                  className="rounded-lg bg-honda-red px-3 py-2 text-xs font-bold text-white"
                >
                  Simpan
                </button>
              )}
            </div>
          </div>
        </section>

        {activeNav !== "dashboard" ? (
          <AdminRouteContent
            activeNav={activeNav}
            filteredSalesmen={filteredSalesmen}
            salesmanRows={salesmanRows}
            prospects={filteredProspects}
            evidenceRows={evidenceRows}
            dealerRows={dealerRows}
            auditRows={auditRows}
            coverageStats={coverageStats}
            coverageSetting={coverageSetting}
            settingDraft={settingDraft}
            setSettingDraft={setSettingDraft}
            saveCoverageSetting={saveCoverageSetting}
            babelTarget={babelTarget}
            babelTargetDraft={babelTargetDraft}
            setBabelTargetDraft={setBabelTargetDraft}
            saveBabelTarget={saveBabelTarget}
            user={user}
            mapLayer={mapLayer}
            setMapLayer={setMapLayer}
            coverageFilter={coverageFilter}
            setCoverageFilter={setCoverageFilter}
            hotspots={COVERAGE_HOTSPOTS}
            scorecard={SCORECARD}
            productDistribution={PRODUCT_DISTRIBUTION}
            settingsTab={settingsTab}
            setSettingsTab={setSettingsTab}
            dealerTargets={dealerTargets}
            salesTargets={salesTargets}
            dealerTargetDraft={dealerTargetDraft}
            setDealerTargetDraft={setDealerTargetDraft}
            salesTargetDraft={salesTargetDraft}
            setSalesTargetDraft={setSalesTargetDraft}
            saveDealerTarget={saveDealerTarget}
            saveSalesTarget={saveSalesTarget}
            deleteDealerTarget={deleteDealerTarget}
            deleteSalesTarget={deleteSalesTarget}
            canManageTargets={canManageTargets}
            targetSalesmanOptions={targetSalesmanOptions}
            dealerOptions={dealerOptions}
            dealerLocked={dealerLocked}
            onOpenSalesman={setSelectedSalesman}
            onOpenProspect={setSelectedProspect}
            onOpenEvidence={setSelectedEvidence}
            managedUsers={managedUsers}
            manageableRoles={manageableRoles}
            userRoleFilter={userRoleFilter}
            setUserRoleFilter={setUserRoleFilter}
            userDealerFilter={userDealerFilter}
            setUserDealerFilter={setUserDealerFilter}
            userStatusFilter={userStatusFilter}
            setUserStatusFilter={setUserStatusFilter}
            userSearch={userSearch}
            setUserSearch={setUserSearch}
            openCreateUser={openCreateUser}
            openEditUser={openEditUser}
            toggleManagedUser={toggleManagedUser}
            setResetTarget={setResetTarget}
            branchHeadOptions={branchHeadOptions}
            teamLeaderOptions={teamLeaderOptions}
          />
        ) : (
          <>
        {/* Map + Activity */}
        <section className="px-6 pt-5 grid grid-cols-1 xl:grid-cols-3 gap-4">
          {/* MAP */}
          <div className="xl:col-span-2 honda-card overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-honda-border">
              <div>
                <p className="font-heading font-extrabold text-base text-honda-ink leading-none">
                  Live Salesman Map
                </p>
                <p className="text-[11px] text-honda-muted mt-1.5">
                  {filteredSalesmen.length} salesman terdeteksi di lapangan
                </p>
              </div>
              <div className="bg-honda-bg rounded-full p-1 flex">
                <button
                  data-testid="map-tab-live"
                  onClick={() => setMapLayer("live")}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold flex items-center gap-1.5 transition-all ${
                    mapLayer === "live"
                      ? "bg-honda-red text-white shadow"
                      : "text-honda-muted hover:text-honda-ink"
                  }`}
                >
                  <Radio size={11} /> Live
                </button>
                <button
                  data-testid="map-tab-heatmap"
                  onClick={() => setMapLayer("heatmap")}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold flex items-center gap-1.5 transition-all ${
                    mapLayer === "heatmap"
                      ? "bg-honda-red text-white shadow"
                      : "text-honda-muted hover:text-honda-ink"
                  }`}
                >
                  <Layers size={11} /> Heatmap
                </button>
              </div>
            </div>
            <div className="px-5 py-3 border-b border-honda-border flex flex-wrap gap-2">
              {[
                ["all", "Semua"],
                ["valid", "Valid"],
                ["expired", "Expired"],
                ["prospect", "Prospek"],
                ["closing", "Closing"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => setCoverageFilter(value)}
                  className={`rounded-full px-3 py-1.5 text-[11px] font-semibold ${
                    coverageFilter === value
                      ? "bg-honda-red text-white"
                      : "bg-honda-bg text-honda-muted"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="h-[420px] w-full" data-testid="admin-map">
              <MapContainer
                center={[HOME_COORD.lat, HOME_COORD.lng]}
                zoom={10}
                scrollWheelZoom={true}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer
                  attribution='&copy; OpenStreetMap'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                {mapLayer === "live" &&
                  filteredSalesmen.filter((s) => s.status !== "out_of_area").map((s) => (
                    <Marker
                      key={s.id}
                      position={[s.coord.lat, s.coord.lng]}
                      icon={buildSalesmanIcon(s.status, s.name[0])}
                    >
                      <Popup>
                        <div className="min-w-[160px]">
                          <b className="text-honda-ink">{s.name}</b>
                          <p className="text-[11px] text-honda-muted mt-1">
                            {s.dealer}
                          </p>
                          <p className="text-[11px] mt-1">
                            Status:{" "}
                            <span
                              style={{ color: (STATUS_META[s.status] || STATUS_META.valid).color }}
                              className="font-bold"
                            >
                              {(STATUS_META[s.status] || STATUS_META.valid).label}
                            </span>
                          </p>
                          <p className="text-[11px] mt-0.5">
                            Kunjungan hari ini: <b>{s.visitsToday}</b>
                          </p>
                          {s.photoUrl && (
                            <a href={s.photoUrl} target="_blank" rel="noreferrer">
                              <SafeImage src={s.photoUrl} alt="Foto evidence check-in" fallbackName={s.name} className="mt-2 h-24 w-full rounded-lg object-cover" fallbackClassName="mt-2 h-24 w-full rounded-lg" />
                            </a>
                          )}
                          <p className="text-[11px] mt-1">
                            Accuracy GPS: <b>{Math.round(s.accuracy || 0)}m</b>
                          </p>
                          <p className="font-mono text-[11px] text-honda-muted mt-0.5">
                            {Number(s.coord.lat).toFixed(6)}, {Number(s.coord.lng).toFixed(6)}
                          </p>
                          <p className="text-[11px] text-honda-muted mt-1">
                            Update {s.lastUpdate}
                          </p>
                        </div>
                      </Popup>
                      <Tooltip
                        direction="top"
                        offset={[0, -16]}
                        opacity={0.95}
                        className="!bg-honda-ink !text-white !border-0 !shadow-lg"
                      >
                        <span className="font-semibold">{s.name}</span>
                      </Tooltip>
                    </Marker>
                  ))}

                {mapLayer === "heatmap" &&
                  COVERAGE_HOTSPOTS.map((h, idx) => (
                    <Circle
                      key={idx}
                      center={[h.lat, h.lng]}
                      radius={2500 + h.intensity * 5000}
                      pathOptions={{
                        color: "#E2231A",
                        fillColor: "#E2231A",
                        fillOpacity: 0.15 + h.intensity * 0.35,
                        weight: 1,
                      }}
                    />
                  ))}
                {mapLayer === "heatmap" &&
                  COVERAGE_HOTSPOTS.map((h, idx) => (
                    <CircleMarker
                      key={`p-${idx}`}
                      center={[h.lat, h.lng]}
                      radius={4}
                      pathOptions={{
                        color: "#fff",
                        fillColor: "#E2231A",
                        fillOpacity: 1,
                        weight: 2,
                      }}
                    />
                  ))}
              </MapContainer>
            </div>
            <div className="px-5 py-3 border-t border-honda-border flex items-center gap-4 text-[11px] text-honda-muted">
              {Object.entries(STATUS_META).map(([k, v]) => (
                <div key={k} className="flex items-center gap-1.5">
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ background: v.color }}
                  />
                  <span className="font-semibold">{v.label}</span>
                </div>
              ))}
              <span className="ml-auto">
                {mapLayer === "heatmap"
                  ? "Density kunjungan 30 hari terakhir"
                  : "Posisi update setiap 30 detik"}
              </span>
            </div>
          </div>

          {/* ACTIVITY */}
          <div className="honda-card overflow-hidden flex flex-col">
            <div className="px-5 py-4 border-b border-honda-border flex items-center justify-between">
              <div>
                <p className="font-heading font-extrabold text-base text-honda-ink leading-none">
                  Aktivitas Realtime
                </p>
                <p className="text-[11px] text-honda-muted mt-1.5">
                  Event stream dari lapangan
                </p>
              </div>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-1 bg-green-50 text-green-700 rounded-full">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" />
                </span>
                LIVE
              </span>
            </div>
            <div
              data-testid="activity-feed"
              className="divide-y divide-honda-border max-h-[420px] overflow-y-auto"
            >
              {ADMIN_ACTIVITY.map((a) => {
                const meta = ACTIVITY_ICONS[a.type] || ACTIVITY_ICONS.prospect;
                const { Icon, color, bg } = meta;
                return (
                  <div
                    key={a.id}
                    className="px-5 py-3.5 flex items-start gap-3 hover:bg-honda-bg transition-colors"
                  >
                    <div
                      className={`w-8 h-8 rounded-lg ${bg} ${color} flex items-center justify-center shrink-0`}
                    >
                      <Icon size={15} strokeWidth={2.2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-honda-ink leading-tight">
                        {a.salesman}
                      </p>
                      <p className="text-[12px] text-honda-muted mt-0.5">
                        {a.action} · {a.detail}
                      </p>
                    </div>
                    <p className="text-[10px] text-honda-subtle whitespace-nowrap pt-1">
                      {a.time}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Charts row */}
        <section className="px-6 pt-5 grid grid-cols-1 xl:grid-cols-3 gap-4">
          {/* Weekly trend */}
          <div className="xl:col-span-2 honda-card p-5">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="font-heading font-extrabold text-base text-honda-ink leading-none">
                  Tren Prospek vs Closing
                </p>
                <p className="text-[11px] text-honda-muted mt-1.5">
                  7 hari terakhir · Semua dealer
                </p>
              </div>
              <div className="flex items-center gap-3 text-[11px] font-semibold">
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm bg-honda-red" />
                  Prospek
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm bg-honda-ink" />
                  Closing
                </span>
              </div>
            </div>
            <div className="h-[260px] mt-3" data-testid="chart-weekly-trend">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={WEEKLY_TREND}
                  margin={{ top: 10, right: 0, left: -20, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 11, fill: "#525252" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#525252" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <ChartTooltip
                    cursor={{ fill: "rgba(226,35,26,0.05)" }}
                    contentStyle={{
                      borderRadius: 10,
                      border: "1px solid #E5E7EB",
                      fontSize: 12,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                    }}
                  />
                  <Bar dataKey="prospek" fill="#E2231A" radius={[6, 6, 0, 0]} barSize={18} />
                  <Bar dataKey="closing" fill="#1A1A1A" radius={[6, 6, 0, 0]} barSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Product distribution */}
          <div className="honda-card p-5">
            <p className="font-heading font-extrabold text-base text-honda-ink leading-none">
              Minat Produk
            </p>
            <p className="text-[11px] text-honda-muted mt-1.5">
              Distribusi prospek bulan ini
            </p>
            <div className="h-[260px] mt-2" data-testid="chart-product-distribution">
              {PRODUCT_DISTRIBUTION.length === 0 ? (
                <EmptyState message="Tidak ada data prospek bulan ini" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={PRODUCT_DISTRIBUTION}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={48}
                      outerRadius={78}
                      paddingAngle={2}
                    >
                      {PRODUCT_DISTRIBUTION.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <ChartTooltip
                      contentStyle={{
                        borderRadius: 10,
                        border: "1px solid #E5E7EB",
                        fontSize: 12,
                      }}
                    />
                    <Legend
                      verticalAlign="bottom"
                      iconType="circle"
                      wrapperStyle={{ fontSize: 10, paddingTop: 8 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </section>

        {/* Dealer Scorecard + Top Salesmen */}
        <section className="px-6 pt-5 grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="honda-card p-5 xl:col-span-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-heading font-extrabold text-base text-honda-ink leading-none">
                  Dealer Scorecard
                </p>
                <p className="text-[11px] text-honda-muted mt-1.5">
                  Kinerja dealer bulan ini
                </p>
              </div>
                      <button
                        data-testid="scorecard-detail-btn"
                        onClick={() => navigate("/admin/dealer")}
                        className="text-xs text-honda-red font-semibold flex items-center gap-0.5 hover:underline"
                      >
                Detail <ChevronRight size={13} />
              </button>
            </div>
            <div className="mt-4 space-y-3.5" data-testid="dealer-scorecard">
              {SCORECARD.length === 0 ? (
                <EmptyState />
              ) : (
                SCORECARD.map((c, i) => {
                  return (
                    <div key={c.dealer + i}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <span className="font-heading font-extrabold text-xs text-honda-muted mr-1">#{i + 1}</span>
                          <Building2 size={13} className="text-honda-subtle" />
                          <p className="text-sm font-semibold text-honda-ink">{c.dealer}</p>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="font-heading font-extrabold text-sm text-honda-ink">
                            {c.achievement}%
                          </span>
                        </div>
                      </div>
                      <div className="flex justify-between text-[10px] text-honda-muted mb-1.5">
                        <span>Cov: {c.coveragePct}%</span>
                        <span>Prospek: {c.prospects}</span>
                        <span>Closing: {c.closing}</span>
                      </div>
                      <div className="relative h-2 bg-honda-bg rounded-full overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0 rounded-full transition-all bg-gradient-to-r from-blue-500 to-blue-700"
                          style={{ width: `${c.achievement}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="honda-card p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-heading font-extrabold text-base text-honda-ink leading-none">
                  Top Salesman
                </p>
                <p className="text-[11px] text-honda-muted mt-1.5">Leaderboard bulan ini</p>
              </div>
              <Award className="text-honda-red" size={18} />
            </div>
            <div className="mt-3 space-y-2" data-testid="top-salesmen">
              {TOP_SALESMEN.map((s, i) => (
                <div
                  key={s.id}
                  className={`flex items-center gap-3 p-2.5 rounded-xl ${
                    i === 0
                      ? "bg-gradient-to-r from-honda-red-light to-white border border-honda-red/20"
                      : "hover:bg-honda-bg"
                  }`}
                >
                  <div
                    className={`w-7 h-7 rounded-lg flex items-center justify-center font-heading font-extrabold text-xs ${
                      i === 0
                        ? "bg-honda-red text-white"
                        : i === 1
                        ? "bg-honda-ink text-white"
                        : "bg-honda-bg text-honda-muted"
                    }`}
                  >
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-honda-ink leading-tight truncate">
                      {s.name}
                    </p>
                    <p className="text-[11px] text-honda-muted mt-0.5">{s.dealer}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading font-extrabold text-sm text-honda-ink leading-none">
                      {s.score}
                    </p>
                    <p className="text-[10px] text-honda-muted mt-0.5">Score</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Active salesman table */}
        <section className="px-6 py-5">
          <div className="honda-card overflow-hidden">
            <div className="px-5 py-4 border-b border-honda-border flex items-center justify-between">
              <div>
                <p className="font-heading font-extrabold text-base text-honda-ink leading-none">
                  Salesman Aktif
                </p>
                <p className="text-[11px] text-honda-muted mt-1.5">
                  {filteredSalesmen.length} dari {LIVE_SALESMEN.length} ditampilkan ·
                  Diperbarui beberapa detik lalu
                </p>
              </div>
              <button
                data-testid="export-salesmen-btn"
                onClick={handleDownloadExcel}
                className="honda-btn-outline text-xs py-2 px-3"
              >
                Export CSV
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="active-salesmen-table">
                <thead className="bg-honda-bg">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-honda-muted">
                    <th className="px-5 py-3 font-bold">Salesman</th>
                    <th className="px-3 py-3 font-bold">Dealer</th>
                    <th className="px-3 py-3 font-bold text-center">Visit Hari Ini</th>
                    <th className="px-3 py-3 font-bold text-center">Prospek</th>
                    <th className="px-3 py-3 font-bold text-center">Closing</th>
                    <th className="px-5 py-3 font-bold text-right">Last Activity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-honda-border">
                  {filteredSalesmen.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="text-center py-10 text-honda-muted text-sm"
                      >
                        Tidak ada salesman cocok dengan filter ini.
                      </td>
                    </tr>
                  ) : (
                    filteredSalesmen.map((s) => {
                      const meta = STATUS_META[s.status] || STATUS_META.valid;
                      return (
                        <tr
                          key={s.id}
                          className="hover:bg-honda-bg transition-colors"
                          data-testid={`row-salesman-${s.id}`}
                        >
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-3">
                              <SafeImage src={s.avatar} alt={s.name} fallbackName={s.name} className="h-9 w-9 rounded-full object-cover" />
                              <div>
                                <button
                                  type="button"
                                  onClick={() => setSelectedSalesman(s)}
                                  className="font-semibold text-honda-ink leading-tight hover:text-honda-red"
                                >
                                  {s.name}
                                </button>
                                <p className="text-[11px] text-honda-muted mt-0.5">
                                  ID #{s.id.toUpperCase()}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className="font-heading font-extrabold text-honda-ink">
                              {s.visitsToday}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className="font-heading font-extrabold text-honda-ink">
                              {s.prospectsToday}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className="font-heading font-extrabold text-honda-ink">
                              {s.closingToday}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-right text-[11px] text-honda-muted">
                            <span className="inline-flex items-center gap-1">
                              <CheckCircle2 size={11} className="text-green-600" />
                              {s.lastUpdate}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
          </>
        )}

        <AdminDetailModal
          title={selectedSalesman ? `Salesman · ${selectedSalesman.name}` : ""}
          subtitle={selectedSalesman?.dealer}
          onClose={() => setSelectedSalesman(null)}
        >
          {selectedSalesman && (
            <DetailGrid
              rows={[
                ["Profil", selectedSalesman.name],
                ["Dealer", selectedSalesman.dealer],
                ["Coverage", `${selectedSalesman.visits ?? selectedSalesman.visitsToday ?? 0} check-in`],
                ["Prospek", selectedSalesman.prospects ?? 0],
                ["Closing", selectedSalesman.closing ?? 0],
                ["Bukti Foto", selectedSalesman.evidence ?? (selectedSalesman.photoUrl ? 1 : 0)],
                ["Riwayat Check-In", selectedSalesman.lastCheckin || selectedSalesman.lastUpdate],
                ["Status", selectedSalesman.active === false ? "Nonaktif" : "Aktif"],
              ]}
            />
          )}
        </AdminDetailModal>

        <AdminDetailModal
          title={selectedProspect ? `Prospek · ${selectedProspect.name}` : ""}
          subtitle={selectedProspect?.salesman_name}
          onClose={() => setSelectedProspect(null)}
        >
          {selectedProspect && (
            <>
              <DetailGrid
                rows={[
                  ["Nama", selectedProspect.name],
                  ["No HP", selectedProspect.phone],
                  ["Unit diminati", productInterest(selectedProspect)],
                  ["Sales", selectedProspect.salesman_name],
                  ["Dealer", resolveDealerMeta(selectedProspect.dealer_id, selectedProspect.dealer_id)?.name || "Dealer tidak ditemukan"],
                  ["Status", selectedProspect.status],
                  ["Tanggal Input", formatDateTime(selectedProspect.created_at)],
                  ["Timeline Follow Up", selectedProspect.updated_at ? `Update terakhir ${formatDateTime(selectedProspect.updated_at)}` : "Belum ada follow-up"],
                ]}
              />
              <div className="mt-4 rounded-xl border border-honda-border p-4">
                <label className="text-xs font-bold uppercase tracking-wider text-honda-muted">Edit Status Prospek</label>
                <div className="mt-2 flex gap-2">
                  <select value={selectedProspect.status} onChange={(e) => updateAdminProspectStatus(selectedProspect, e.target.value).catch((err) => toast.error(err?.response?.data?.detail || "Gagal update status prospek."))} className="h-10 rounded-xl border border-honda-border bg-white px-3 text-sm">
                    {Object.entries(PROSPECT_STATUS).map(([key, meta]) => <option key={key} value={key}>{meta.label}</option>)}
                  </select>
                </div>
              </div>
            </>
          )}
        </AdminDetailModal>

        <AdminDetailModal
          title={selectedEvidence ? "Preview Bukti Foto" : ""}
          subtitle={selectedEvidence ? `${selectedEvidence.salesman_name || "Sales"} · ${formatDateTime(selectedEvidence.checkin_time)}` : ""}
          image={selectedEvidence?.photo_url}
          onClose={() => setSelectedEvidence(null)}
        >
          {selectedEvidence && (
            <DetailGrid
              rows={[
                ["Sales", selectedEvidence.salesman_name],
                ["Dealer", selectedEvidence.dealer_id],
                ["GPS", `${Number(selectedEvidence.latitude).toFixed(6)}, ${Number(selectedEvidence.longitude).toFixed(6)}`],
                ["Accuracy", `${Math.round(selectedEvidence.accuracy || 0)}m`],
                ["Timestamp", formatDateTime(selectedEvidence.checkin_time)],
                ["Status", STATUS_META[selectedEvidence.visual_status || selectedEvidence.status]?.label],
              ]}
            />
          )}
        </AdminDetailModal>

        {userModal && (
          <div className="fixed inset-0 z-[920] flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-honda-border px-5 py-4">
                <div>
                  <p className="font-heading text-lg font-extrabold text-honda-ink">{userModal.mode === "create" ? "Tambah User" : "Edit User"}</p>
                  <p className="text-xs text-honda-muted">Kelola role, dealer, struktur bawahan, dan status akun.</p>
                </div>
                <button onClick={() => setUserModal(null)} className="rounded-full border border-honda-border px-3 py-1.5 text-xs font-bold text-honda-muted">Tutup</button>
              </div>
              <div className="grid grid-cols-1 gap-3 p-5 md:grid-cols-2">
                <label><span className="honda-label">Nama Lengkap</span><input value={userDraft.name} onChange={(e) => setUserDraft((v) => ({ ...v, name: e.target.value }))} className="honda-input" /></label>
                <label><span className="honda-label">Email</span><input value={userDraft.email} onChange={(e) => setUserDraft((v) => ({ ...v, email: e.target.value }))} className="honda-input" /></label>
                {userModal.mode === "create" && <label><span className="honda-label">Password</span><input type="password" value={userDraft.password} onChange={(e) => setUserDraft((v) => ({ ...v, password: e.target.value }))} className="honda-input" /></label>}
                <label><span className="honda-label">Nomor HP</span><input value={userDraft.phone} onChange={(e) => setUserDraft((v) => ({ ...v, phone: e.target.value }))} className="honda-input" /></label>
                <label>
                  <span className="honda-label">Role</span>
                  <select value={userDraft.role} onChange={(e) => setUserDraft((v) => ({ ...v, role: e.target.value, branch_head_id: "", team_leader_id: "" }))} className="honda-input">
                    {manageableRoles.map((role) => <option key={role} value={role}>{ROLE_LABELS[role]}</option>)}
                  </select>
                </label>
                <label>
                  <span className="honda-label">Dealer</span>
                  <select value={userDraft.dealer_id} disabled={userDraft.role === "main_dealer" || dealerLocked} onChange={(e) => setUserDraft((v) => ({ ...v, dealer_id: e.target.value, branch_head_id: "", team_leader_id: "" }))} className="honda-input disabled:bg-honda-bg">
                    <option value="">Pilih Dealer</option>
                    {dealerOptions.filter((item) => item.value !== "all").map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                </label>
                <label>
                  <span className="honda-label">Kepala Cabang</span>
                  <select value={userDraft.branch_head_id} disabled={userDraft.role !== "team_leader"} onChange={(e) => setUserDraft((v) => ({ ...v, branch_head_id: e.target.value }))} className="honda-input disabled:bg-honda-bg">
                    <option value="">Pilih Kepala Cabang</option>
                    {branchHeadOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                </label>
                <label>
                  <span className="honda-label">Team Leader</span>
                  <select value={userDraft.team_leader_id} disabled={userDraft.role !== "sales"} onChange={(e) => {
                    const leader = users.find((item) => item.id === e.target.value);
                    setUserDraft((v) => ({ ...v, team_leader_id: e.target.value, branch_head_id: leader?.branch_head_id || v.branch_head_id, dealer_id: leader?.dealer_id || v.dealer_id }));
                  }} className="honda-input disabled:bg-honda-bg">
                    <option value="">Pilih Team Leader</option>
                    {teamLeaderOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                </label>
                <label>
                  <span className="honda-label">Status Akun</span>
                  <select value={userDraft.active ? "active" : "inactive"} onChange={(e) => setUserDraft((v) => ({ ...v, active: e.target.value === "active" }))} className="honda-input">
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </label>
              </div>
              <div className="flex justify-end gap-2 border-t border-honda-border px-5 py-4">
                <button onClick={() => setUserModal(null)} className="rounded-xl border border-honda-border px-4 py-2 text-sm font-bold text-honda-muted">Batal</button>
                <button onClick={() => saveManagedUser().catch((err) => toast.error(err?.response?.data?.detail || "Gagal menyimpan user."))} className="rounded-xl bg-honda-red px-4 py-2 text-sm font-bold text-white">Simpan User</button>
              </div>
            </div>
          </div>
        )}

        {resetTarget && (
          <div className="fixed inset-0 z-[930] flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
              <p className="font-heading text-lg font-extrabold text-honda-ink">Reset Password</p>
              <p className="mt-1 text-sm text-honda-muted">{resetTarget.name} · {resetTarget.email}</p>
              <input type="password" value={resetPasswordDraft} onChange={(e) => setResetPasswordDraft(e.target.value)} placeholder="Password baru" className="honda-input mt-4" />
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => { setResetTarget(null); setResetPasswordDraft(""); }} className="rounded-xl border border-honda-border px-4 py-2 text-sm font-bold text-honda-muted">Batal</button>
                <button onClick={() => resetManagedPassword().catch((err) => toast.error(err?.response?.data?.detail || "Gagal reset password."))} className="rounded-xl bg-honda-red px-4 py-2 text-sm font-bold text-white">Simpan</button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="px-6 pb-6 text-center text-[11px] text-honda-subtle">
          SALES TRACK · Honda Babel Sales Coverage Intelligence System · v1.0 (Demo Build)
        </footer>
      </main>
    </div>
  );
}
